import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  ChevronDown,
  FileText,
  History,
  ChevronLeft,
  ChevronRight,
  Search,
  Settings2,
  X,
} from "lucide-react";
import api from "@/utils/axios-utils";
import PrimaryButton from "@/components/PrimaryButton";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useAuthStore } from "@/store/useAuthStore";
import { useAccounts, type AccessibleAccount } from "@/hooks/useAccounts";
import { withAsOf } from "@/utils/as-of";
import { ENABLE_NO_EXPECTATION_MODE } from "@/flags";
import { usePacingModeEnabled } from "@/hooks/useRuntimeFlags";
import LessonSequencingSkeleton from "./LessonSequencingSkeleton";
import { sequencingOwnership } from "./sequencingOwnership";
import {
  groupToday,
  holdsLessonsOn,
  isWholeDaySkipped,
  servesWeekday,
  skipCoverage,
  studentsWithMovableLessonsOn,
  skipTag,
  type SkipCoverage,
} from "./skipCoverage";
import {
  AVATAR_PALETTE,
  MAX_DAYS_PER_WEEK,
  MAX_LESSONS_PER_DAY,
  isModeSwitch,
  isPointerMode,
  isWeeklyAssignment,
  modeSwitchBody,
  reviewSuggestion,
  settingsFormSeed,
  sharedCurrentLesson,
  sharedCurrentLessonId,
  lessonsPerWeek,
  avatarPaletteIndex,
  canResetStartingPoint,
  changeLessonImpact,
  changeTargetClassroom,
  selectedStudentIdsIn,
  findOptionForPath,
  getInitials,
  curriculumLevels,
  describeSequencingEvent,
  pathNodesAtDepth,
  resetStartingPoint,
  startingPointBadge,
  truncateMiddle,
} from "./lessonSequencingRules";

type Mode = "completion" | "pacing";
// "none" is No expectation mode (P-PRD-525): one lesson at a time, moved on by
// finishing it. Ask `isPointerMode` before `isWeeklyAssignment`, never instead:
// "none" is not weekly, and a reader that takes not-weekly for daily would page
// days in a class that has none.
type AssignmentMode = "weekly" | "daily" | "none";
// The page's dialogs that save something, for `reportDialogError`.
type Dialog = "settings" | "review" | "change";

interface Settings {
  default_enabled: boolean;
  enabled: boolean;
  sequence_id: number | null;
  expected_days_per_week: number;
  expected_lessons_per_day: number;
  expected_lessons_per_week: number;
  incomplete_mode: Mode;
  assignment_mode: AssignmentMode;
  // No expectation mode only: the stored choice behind the forced completion.
  paced_incomplete_mode?: Mode | null;
}

interface ClassroomSummary {
  id: number;
  name: string;
  settings: Settings;
  configured: boolean;
}

interface CurriculumPathNode {
  curriculum_node_id: number;
  depth: number;
  label: string;
  name: string;
}

interface LessonItem {
  row_id?: number | null;
  curriculum_node_id?: number | null;
  lesson_id?: number | null;
  title: string;
  status: string;
  assignment_source: string;
  assigned_for_date?: string | null;
  path: CurriculumPathNode[];
}

interface StudentRow {
  student_id: number;
  user_id: number;
  name: string;
  status: "pending_placement" | "active" | "exhausted";
  placement_status: "suggested" | "auto_approved" | "approved";
  pending_reason?: string | null;
  suggested_curriculum_node_id?: number | null;
  // Where the student would start No expectation mode (dated roster only).
  entry_curriculum_node_id?: number | null;
  previous: LessonItem[];
  current: LessonItem[];
  upcoming: LessonItem[];
  /** Where the student actually is, which `current` stops being once the
   *  roster is paged -- that holds whatever falls on the focused date. This is
   *  the row `change_lesson` displaces whatever day is on screen. */
  current_lesson?: LessonItem | null;
  has_earlier: boolean;
  has_later: boolean;
  // Whether a one-day shift can move this student's schedule at all. The
  // server answers with the rule it will apply on save.
  can_shift_back?: boolean;
  can_shift_forward?: boolean;
}

interface LessonOption {
  curriculum_node_id: number;
  lesson_id?: number | null;
  title: string;
  position: number;
  path: CurriculumPathNode[];
  /** Unpublished. No expectation mode refuses a pick of one (the walk steps
   *  over drafts), so its picker leaves them out. */
  is_draft?: boolean;
}

/**
 * What `events()` returns in one page -- it caps at 200 and has no total, so
 * this is how far the banner's counter can be trusted. Mirrors the `limit` in
 * `backend/apps/tutor/sequencing/service.py::events`.
 */
const EVENT_PAGE_SIZE = 200;

/** How long a toast carrying Undo stays up.
 *
 *  Longer than the 5s default: these announce a bulk write across a roster, and
 *  the offer is worth nothing if it has gone by the time the teacher has read
 *  what happened. Once it goes the write stands -- every action it covers is
 *  still reversible from the page itself. */
const UNDO_TOAST_MS = 12000;

interface WeekPreviewLesson {
  slot: number;
  // Which day this lesson lands on, for a daily classroom. The pick governs the
  // days *after* it too, so this list can span several -- and rendering it flat
  // under "Lessons this day" told a two-a-day, five-day classroom that all ten
  // belonged to the day being edited. Null on a weekly classroom, which hands
  // out one undated batch.
  day: string | null;
  curriculum_node_id: number;
  lesson_id: number | null;
  title: string;
  source: "teacher_choice" | "auto_filled";
  already_completed: boolean;
}

interface WeekPreview {
  student_id: number;
  // Which batch the pick starts. A weekly classroom hands out its whole week
  // at once; a daily one hands out that day's lessons; No expectation mode,
  // the one lesson.
  unit: "week" | "day" | "lesson";
  lessons_per_unit: number;
  target: number;
  lessons: WeekPreviewLesson[];
}

interface SequencingEvent {
  id: number;
  actor_user_id: number | null;
  actor_name: string | null;
  action: string;
  created_at: string;
  payload?: Record<string, unknown>;
}

interface Overview {
  teacher_settings: Settings;
  classrooms: ClassroomSummary[];
  /** False when the org has no curriculum left to schedule from. */
  curriculum_available: boolean;
}

interface Detail {
  classroom_id: number;
  classroom_name: string;
  settings: Settings;
  configured: boolean;
  students: StudentRow[];
  lesson_options: LessonOption[];
  day_offset: number;
  previous_date: string | null;
  current_date: string | null;
  next_date: string | null;
  skipped_dates?: string[];
  group_skipped_dates?: string[];
}

/** A roster row always knows which classroom it came from, so cross-group
 *  views can route mutations back to the right classroom endpoint. */
type RosterStudent = StudentRow & {
  classroom_id: number;
  classroom_name: string;
};

const getError = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    return response?.data?.detail || fallback;
  }
  return fallback;
};

const parseDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "AUG 31 TO SEP 4" from the week's Monday. */
const formatWeekLabel = (value?: string | null) => {
  if (!value) return "";
  const [year, month, day] = value.split("-").map(Number);
  const start = new Date(year, (month ?? 1) - 1, day ?? 1);
  const end = new Date(start);
  end.setDate(end.getDate() + 4);
  const part = (date: Date) =>
    date
      .toLocaleDateString(undefined, { month: "short", day: "numeric" })
      .toUpperCase();
  return `${part(start)} TO ${part(end)}`;
};

const formatDayLabel = (value?: string | null) => {
  const date = parseDate(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
};

/** The API accepts +/-260 school days; keep a hand-edited URL inside that. */
const clampDayOffset = (value: number) =>
  Number.isFinite(value) ? Math.trunc(Math.min(260, Math.max(-260, value))) : 0;

/**
 * Identity of one review row.
 *
 * A row is an enrolment, not a student: `reviewRoster` flattens every
 * classroom, and `StudentSequence` is keyed per (classroom, student), so one
 * student in three groups has three sequences and three starting points.
 * Keying selection by `student_id` alone collapsed those into one -- the first
 * suggestion seeded won and `activate` posted it to all three classrooms,
 * overwriting the student's real position in the other two.
 *
 * Inert while a teacher's classrooms share a sequence, which the org-scoped
 * binding reconcile makes the normal case: same sequence and the same
 * user-keyed history give the same suggestion for every row. It is reachable
 * once an organization binds a second curriculum, because
 * `update_classroom_settings` accepts any sequence bound to the org and a
 * teacher can then put two classrooms on different ones.
 */
const rowKey = (student: { classroom_id: number; student_id: number }) =>
  `${student.classroom_id}-${student.student_id}`;

const formatPath = (path: CurriculumPathNode[]) =>
  path.map((node) => node.name).join(" › ");

const LessonList = ({
  lessons,
  emptyLabel = "No lessons",
  withPath = false,
}: {
  lessons: LessonItem[];
  /**
   * What an empty cell says. With the toggle off there are no rows to show —
   * disabling unassigns the never-started ones — and the spec asks for
   * "Auto-sequencing off" rather than a bare "No lessons", which reads as a
   * curriculum gap the teacher should go and fix.
   */
  emptyLabel?: string;
  /**
   * Head each card with the levels above the lesson -- Grade, Mission -- so
   * the title reads as the end of that breadcrumb. No expectation mode only
   * (R29): its columns are positions rather than dates, and the breadcrumb is
   * what says where in the curriculum each one sits.
   */
  withPath?: boolean;
}) =>
  lessons.length ? (
    <div className="flex flex-col gap-2">
      {lessons.map((lesson) => {
        const key = `${lesson.row_id ?? "preview"}-${lesson.curriculum_node_id ?? lesson.title}`;
        // The path ends at the lesson's own node, which the title already
        // names; repeating it would print the lesson twice on one card.
        const leaf = lesson.path[lesson.path.length - 1];
        const levels = !withPath
          ? []
          : leaf && leaf.curriculum_node_id === lesson.curriculum_node_id
            ? lesson.path.slice(0, -1)
            : lesson.path;
        // One chip style for every day. A projected lesson used to be drawn
        // dashed on a tinted fill with a "Projected" caption, which made the
        // three date columns of a single row read as three different kinds of
        // thing when they are all just "the lesson on this date".
        //
        // `break-words`, because wrapping alone does not cover these titles.
        // #1820 fixed the Student column and reasoned the day cells were safe
        // -- "their lesson chips wrap rather than nowrap, so a long title uses
        // another line instead of stretching its column" -- which holds only
        // where there is a space to wrap at. The curriculum joins the tail of a
        // lesson name with underscores (`G4M5L4 - Different
        // Decompositions_(SAG)_Finalized`), and `overflow-wrap: normal` offers
        // no break inside a token like that, so it overflowed the chip instead
        // of taking a second line. `break-words` breaks such a token only when
        // it cannot fit, leaving every title that does have spaces wrapping as
        // before; `break-all` would cut mid-word always.
        return (
          <div
            key={key}
            title={formatPath(lesson.path) || lesson.title}
            className="break-words rounded-lg border border-[#DED8CC] bg-white px-3 py-3 text-sm font-extrabold leading-5 text-[#294B39]"
          >
            {levels.length > 0 && (
              <span className="mb-1 block">
                <CurriculumBreadcrumb compact path={levels} />
              </span>
            )}
            {lesson.title}
          </div>
        );
      })}
    </div>
  ) : (
    // The export's chip geometry exactly -- same radius, border width, padding
    // and type size -- so an empty day and a filled one draw the same box and
    // their edges line up down a column. It was bare centred text, which sat at
    // a different height and had no edge at all, so a lesson card looked
    // narrower than the cell beside it.
    //
    // Dashed and unfilled is what carries "nothing here": the border marks the
    // slot, and leaving the fill off lets the cell's own tint through, so an
    // empty current day still reads as the current day.
    <p className="rounded-lg border border-dashed border-[#DED8CC] px-3 py-3 text-center text-sm font-bold leading-5 text-[#8A958F]">
      {emptyLabel}
    </p>
  );

const DayColumnLabel = ({
  date,
  current = false,
  skip,
  weekly = false,
  heading,
  onClick,
}: {
  date?: string | null;
  current?: boolean;
  /** How many of the groups on screen skip this date; see `skipCoverage`. */
  skip?: SkipCoverage;
  weekly?: boolean;
  /** A column headed by its role, with no date: No expectation mode's
   *  Previous, Current and Next lessons (R29). */
  heading?: string;
  onClick?: () => void;
}) => {
  // A weekly column spans Mon-Fri, so it is headed by the range rather than by
  // one weekday and date.
  const [weekday, monthDay] = heading
    ? [heading, null]
    : weekly
      ? ["Week", formatWeekLabel(date)]
      : formatDayLabel(date).split(", ");
  // Only a day every group has off gets the no-school box. A day some groups
  // skip keeps its normal box and names the share, because the others still
  // have lessons in this column.
  const skipped = Boolean(skip && isWholeDaySkipped(skip));
  const tag = skip ? skipTag(skip) : null;
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em]">
        {current && (
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 rounded-sm bg-[#28543D]"
          />
        )}
        {weekday}
      </span>
      {monthDay && (
        <span className="mt-1 text-sm font-extrabold text-[#294B39]">
          {monthDay}
        </span>
      )}
      {tag && (
        <span
          className="mt-1 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide"
          style={{ backgroundColor: "#FFF4E5", color: "#9A5B00" }}
        >
          {tag}
        </span>
      )}
    </>
  );
  const style = {
    backgroundColor: skipped ? "#FFFBF3" : current ? "#EEF0E3" : "#FFFFFF",
    borderColor: skipped ? "#E8C89A" : current ? "#28543D" : "#DED8CC",
    color: current ? "#28543D" : "#687368",
  };
  const shell =
    "flex min-h-14 w-full flex-col items-center justify-center rounded-lg border px-3 leading-none";

  // A plain span when there is nothing to do with it -- a button that opens
  // nothing still reads as interactive to a keyboard and a screen reader.
  if (!onClick) {
    return (
      <span className={shell} style={style}>
        {body}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        skipped
          ? `${formatDayLabel(date)} is skipped. Review or remove the skip`
          : tag
            ? `${formatDayLabel(date)}: ${tag}. Open skip options`
            : `Skip ${formatDayLabel(date)}`
      }
      className={`${shell} transition-colors hover:border-[#28543D] hover:bg-[#EEF0E3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28543D]/40`}
      style={style}
    >
      {body}
    </button>
  );
};

const CurriculumBreadcrumb = ({
  path,
  fallback,
  compact = false,
}: {
  path?: CurriculumPathNode[];
  fallback?: string;
  compact?: boolean;
}) => {
  if (!path?.length) {
    return (
      <span className={compact ? "text-xs font-bold" : "text-base font-bold"}>
        {fallback ?? "—"}
      </span>
    );
  }
  return (
    <span
      className={`flex flex-wrap items-center font-bold ${compact ? "gap-x-1 text-[10px] leading-4" : "gap-x-2 text-base leading-6"}`}
      title={formatPath(path)}
    >
      {path.map((node, index) => (
        <span key={`${node.curriculum_node_id}-${index}`}>
          <span
            style={{ color: index === path.length - 1 ? "#294B39" : "#687368" }}
          >
            {node.name}
          </span>
          {index < path.length - 1 && (
            <span aria-hidden="true" className="mx-1 text-[#9AA6A0]">
              ›
            </span>
          )}
        </span>
      ))}
    </span>
  );
};

const Avatar = ({ name, selected }: { name: string; selected: boolean }) => (
  <span
    aria-hidden="true"
    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold transition-colors"
    style={
      selected
        ? {
            backgroundColor: "#28543D",
            color: "#FFFFFF",
            boxShadow: "0 0 0 3px #D7F0E2",
          }
        : AVATAR_PALETTE[avatarPaletteIndex(name)]
    }
  >
    {getInitials(name)}
  </span>
);

/**
 * Multi-select in the shape the design uses for "Choose groups": one control
 * for which groups are on screen, a select-all row, and a floor of one — an
 * empty scope would show an empty roster with no way back.
 */
const GroupMultiSelect = ({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: { id: number; name: string }[];
  values: number[];
  onChange: (next: number[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selected = options.filter((option) => values.includes(option.id));
  const allSelected =
    options.length > 0 && options.every((option) => values.includes(option.id));

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePress);
  }, [open]);

  return (
    <div ref={containerRef} className="relative flex min-w-[220px] flex-col">
      <span className="mb-2 font-bold text-[#294B39]">{label}</span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex h-11 w-full items-center justify-between gap-3 rounded-[6px] border bg-white px-4 text-left text-sm font-semibold text-[#294B39]"
        style={{ borderColor: open ? "#28543D" : "#D6CEBF" }}
      >
        <span className="min-w-0 truncate">
          {allSelected
            ? "All groups"
            : selected.length === 1
              ? selected[0].name
              : `${selected.length} selected`}
        </span>
        <ChevronDown size={18} className="shrink-0 text-[#687368]" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-y-auto rounded-[6px] border bg-[#FBF8F1] p-1.5 shadow-lg"
          style={{ borderColor: "#DED8CC" }}
        >
          {options.length === 0 && (
            <p className="px-3 py-6 text-center text-sm font-semibold text-[#687368]">
              No groups available.
            </p>
          )}
          {options.length > 0 && (
            <button
              type="button"
              role="option"
              aria-selected={allSelected}
              onClick={() =>
                onChange(
                  allSelected
                    ? values.slice(0, 1)
                    : options.map((option) => option.id),
                )
              }
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-bold text-[#294B39] hover:bg-[#FBF8F1]"
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${allSelected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
              >
                <Check size={13} strokeWidth={3} />
              </span>
              All groups
            </button>
          )}
          {options.map((option) => {
            const isSelected = values.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  // Deselecting the last one would empty the roster.
                  if (isSelected && values.length === 1) return;
                  onChange(
                    isSelected
                      ? values.filter((id) => id !== option.id)
                      : [...values, option.id],
                  );
                }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-[#294B39] hover:bg-[#FBF8F1]"
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${isSelected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
                >
                  <Check size={13} strokeWidth={3} />
                </span>
                <span className="min-w-0 truncate">{option.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PagerButton = ({
  direction,
  onClick,
  disabled,
}: {
  direction: "left" | "right";
  onClick: () => void;
  disabled: boolean;
}) => {
  const Icon = direction === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={
        direction === "left" ? "Previous school day" : "Next school day"
      }
      onClick={onClick}
      disabled={disabled}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border disabled:cursor-not-allowed"
      style={{
        borderColor: disabled ? "#DED8CC" : "#9AA884",
        backgroundColor: disabled ? "#F3EDE2" : "#EEF0E3",
        color: disabled ? "#A7B2AC" : "#28543D",
      }}
    >
      <Icon size={19} strokeWidth={2.5} />
    </button>
  );
};

/**
 * Cascading curriculum picker driven by the path each lesson option carries.
 * Level labels and depth come from the curriculum itself, so no hierarchy
 * shape is hardcoded here.
 */
/**
 * The export's single-select dropdown: a white panel with a check box per
 * option, rather than the OS menu a native `<select>` opens. On macOS that menu
 * is dark grey and no CSS reaches it, so the design could not be matched while
 * the field stayed native.
 *
 * Replacing a `<select>` means re-implementing what it gave us for free. The
 * keyboard handling below is not decoration: a listbox built from buttons has
 * ARIA roles but none of the behaviour, and shipping the roles alone would read
 * as accessible while being unusable without a mouse.
 */
const PathDropdown = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  placeholder,
}: {
  label: string;
  value: number | "";
  options: Array<{ value: number; label: string }>;
  onChange: (value: number) => void;
  /** A level whose parent has not been chosen yet has nothing to offer. */
  disabled?: boolean;
  placeholder?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // The native `<label><select>` associated these for free. Without it a
  // screen reader tabbing to the closed control announces the selected lesson
  // but not which level it belongs to — ambiguous the moment there is more
  // than one of these side by side, which is the normal case here.
  const labelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const triggerLabel = options[selectedIndex]?.label ?? "Choose lesson";

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePress);
  }, [open]);

  // Focus follows the active option so arrow keys read out as you move, which
  // is the part `<select>` was doing for us.
  useEffect(() => {
    if (open) optionRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const openAt = (index: number) => {
    setActiveIndex(index < 0 ? 0 : index);
    setOpen(true);
  };

  const commit = (index: number) => {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative flex flex-col gap-3 font-bold">
      <span id={labelId} className="text-sm text-[#294B39]">
        {label}
      </span>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openAt(selectedIndex))}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openAt(selectedIndex);
          }
        }}
        className="flex h-14 w-full items-center justify-between gap-4 rounded-[6px] border px-5 text-left text-lg font-semibold transition-colors disabled:cursor-not-allowed"
        style={{
          borderColor: open ? "#28543D" : "#D6CEBF",
          backgroundColor: disabled ? "#F3EDE2" : "#FFFFFF",
          color: disabled ? "#8A958F" : "#294B39",
          boxShadow: open ? "0 0 0 3px rgba(28,156,109,0.10)" : "none",
        }}
      >
        <span className="min-w-0 truncate">
          {triggerLabel || placeholder || ""}
        </span>
        <span className="shrink-0" style={{ color: "#28543D" }}>
          <ChevronDown size={20} />
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-labelledby={labelId}
          className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-[8px] border bg-[#FBF8F1] p-2 shadow-[0_12px_30px_rgba(16,24,40,0.14)]"
          style={{ borderColor: "#DED8CC" }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
              triggerRef.current?.focus();
            } else if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((current) =>
                Math.min(current + 1, options.length - 1),
              );
            } else if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((current) => Math.max(current - 1, 0));
            } else if (event.key === "Home") {
              event.preventDefault();
              setActiveIndex(0);
            } else if (event.key === "End") {
              event.preventDefault();
              setActiveIndex(options.length - 1);
            } else if (event.key === "Tab") {
              setOpen(false);
            }
          }}
        >
          <div className="max-h-80 overflow-y-auto">
            {options.length === 0 && (
              <p className="px-4 py-8 text-center text-sm font-semibold text-[#687368]">
                No options available.
              </p>
            )}
            {options.map((option, index) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => commit(index)}
                  className="flex min-h-16 w-full items-center gap-5 px-4 py-3 text-left text-lg font-semibold text-[#294B39] transition-colors hover:bg-[#F3EDE2]"
                  style={{
                    backgroundColor: isSelected ? "#F3EDE2" : "#FFFFFF",
                  }}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 bg-white text-[#28543D]"
                    style={{ borderColor: "#28543D" }}
                  >
                    {isSelected && <Check size={14} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const CurriculumPathPicker = ({
  options,
  selected,
  onChange,
  // More than one lesson in the batch means the teacher is choosing the *first*
  // of several, not the only one. Renaming the deepest level says so, which the
  // list underneath then shows.
  picksFirstOfMany = false,
}: {
  options: LessonOption[];
  selected: LessonOption | null;
  onChange: (option: LessonOption) => void;
  picksFirstOfMany?: boolean;
}) => {
  const lessonLevelLabel = (label: string) =>
    picksFirstOfMany && label.toLowerCase() === "lesson"
      ? "First lesson"
      : label;
  /**
   * The curriculum's own levels, in order -- "Grade", "Mission", "Topic",
   * "Lesson", however many it defines.
   *
   * Read off the options rather than off `selected.path`. Deriving it from the
   * selection meant the shape of the picker depended on whether anything was
   * picked: with nothing chosen there was no path, so it collapsed to a single
   * flat "Lesson" dropdown listing every lesson in the curriculum, and the
   * Grade field only appeared *after* a choice had already been made. The
   * hierarchy is a property of the curriculum and is known before the teacher
   * touches anything.
   *
   * Longest path wins, so a malformed option cannot shorten the scaffold.
   */
  const levels = useMemo(() => curriculumLevels(options), [options]);

  // Nothing to cascade through: a curriculum whose options carry no path at all
  // still has to be pickable.
  if (!levels.length) {
    return (
      <PathDropdown
        label={lessonLevelLabel("Lesson")}
        value={selected?.curriculum_node_id ?? ""}
        placeholder="Choose lesson"
        options={options.map((option) => ({
          value: option.curriculum_node_id,
          label: option.title,
        }))}
        onChange={(nodeId) => {
          const next = options.find(
            (option) => option.curriculum_node_id === nodeId,
          );
          if (next) onChange(next);
        }}
      />
    );
  }

  const chosen = selected?.path ?? [];

  return (
    <div className="grid gap-6 sm:grid-cols-2">
      {levels.map((label, depth) => {
        // Prefix and option values are node ids: two siblings may share a
        // name, and keying on names made the second one unreachable.
        const prefix = chosen
          .slice(0, depth)
          .map((item) => item.curriculum_node_id);
        // A level can only offer anything once every level above it is settled.
        // Without this, depth 1 with no grade chosen would list every topic in
        // every grade -- `matchesPrefix` is vacuously true against an empty
        // prefix.
        const ready = prefix.length === depth;
        const siblings = ready ? pathNodesAtDepth(options, prefix) : [];
        return (
          <PathDropdown
            // Keyed by depth, not by the selected node. The node id changes as
            // soon as the teacher picks something, which made this a new
            // element: React unmounted the field they were using, and the
            // focus `commit()` had just restored to its trigger went to the
            // document body. Depth identifies the field, which is what a key
            // is for.
            key={`depth-${depth}`}
            label={lessonLevelLabel(label)}
            value={chosen[depth]?.curriculum_node_id ?? ""}
            placeholder={`Choose ${label.toLowerCase()}`}
            disabled={!ready}
            options={siblings.map((sibling) => ({
              value: sibling.curriculum_node_id,
              label: sibling.name,
            }))}
            onChange={(nodeId) => {
              const next = findOptionForPath(options, prefix, nodeId);
              if (next) onChange(next);
            }}
          />
        );
      })}
    </div>
  );
};

const SkipDayModal = ({
  label,
  isFuture,
  skippingGroups,
  totalGroups,
  groupLabel,
  groupCount,
  selectedCount,
  saving,
  onConfirm,
  onRemove,
  othersSelectedCount,
  offPaceGroups,
  leftOut,
  onSkipOthers,
  onClose,
}: {
  label: string;
  isFuture: boolean;
  /** The groups on screen that already skip this day: whether only some of
   *  their students do, and whether the roster still shows lessons on it. */
  skippingGroups: {
    name: string;
    someStudents: boolean;
    hasLessons: boolean;
  }[];
  /** How many groups are on screen. */
  totalGroups: number;
  groupLabel: string;
  groupCount: number;
  selectedCount: number;
  saving: boolean;
  onConfirm: (scope: "group" | "selected") => void;
  onRemove: () => void;
  /** Selected students outside the groups whose whole day is already off --
   *  some may skip already, which `skip_day` treats as a no-op. */
  othersSelectedCount: number;
  /** Groups whose pace never teaches this weekday but hold a lesson on it
   *  anyway: `skipWrites` skips only those students, not the group. */
  offPaceGroups: string[];
  /** Groups the column shows that this skip cannot reach: the day has passed
   *  for them, or holds nothing of theirs a skip would move. */
  leftOut: string[];
  /** Skip the day for the groups on screen not already skipping it -- the
   *  whole of each, or only their selected students. */
  onSkipOthers: (scope: "group" | "selected") => void;
  onClose: () => void;
}) => {
  const [scope, setScope] = useState<"group" | "selected">(
    selectedCount > 0 ? "selected" : "group",
  );

  if (skippingGroups.length) {
    // With several groups on screen, say which skip the day: the others still
    // have lessons in this column, and Remove skip reaches only these.
    const others = totalGroups - skippingGroups.length;
    // A group where only some students skip is not a day off: its other
    // students still have lessons. It is also left out of "Skip the other
    // groups" -- a group skip recorded over it would shift the students
    // already skipped a second day.
    const someStudents = skippingGroups.some((group) => group.someStudents);
    // A skip is a record, not proof the day is empty: Change lesson can still
    // place a lesson on it (`holdsLessonsOn`).
    const hasLessons = skippingGroups.some((group) => group.hasLessons);
    const names = skippingGroups.map((group) =>
      group.someStudents
        ? `${group.name} (some students)`
        : group.hasLessons
          ? `${group.name} (lessons still placed)`
          : group.name,
    );
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-day-title"
      >
        <div className="w-full max-w-md rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-6 shadow-2xl">
          <h2
            id="skip-day-title"
            className="text-2xl font-extrabold text-[#294B39]"
          >
            Scheduled skip
          </h2>
          <p
            className="mt-2 inline-block rounded-full px-3 py-1 text-xs font-extrabold uppercase tracking-wide"
            style={{ backgroundColor: "#FFF4E5", color: "#9A5B00" }}
          >
            {label} · {someStudents || hasLessons ? "Skipped" : "No school"}
          </p>
          {/* Every group the column shows, not only the ones a skip reaches:
              a group left out is still on screen. */}
          {totalGroups + leftOut.length > 1 ? (
            <p className="mt-3 text-sm leading-6 text-[#687368]">
              {skippingGroups.length === 1
                ? `${names[0]} skips ${label}.`
                : `${skippingGroups.length} groups skip ${label}: ${names.join(", ")}.`}{" "}
              {others > 0 &&
                `The other ${others === 1 ? "group" : `${others} groups`} on screen still ${others === 1 ? "has" : "have"} lessons that day. `}
              {leftOut.length > 0 &&
                `${leftOut.join(", ")} can't skip ${label} and ${leftOut.length === 1 ? "is" : "are"} left as ${leftOut.length === 1 ? "it is" : "they are"}. `}
              {others > 0 &&
                offPaceGroups.length > 0 &&
                `${offPaceGroups.join(", ")} ${offPaceGroups.length === 1 ? "doesn't" : "don't"} teach on ${label}: skipping ${offPaceGroups.length === 1 ? "it skips" : "them skips"} only the students with a lesson that day. `}
              Removing the skip restores the original lesson dates for{" "}
              {skippingGroups.length === 1 ? "that group" : "those groups"}.
            </p>
          ) : (
            <p className="mt-3 text-sm leading-6 text-[#687368]">
              {someStudents
                ? `Some students skip ${label}; the rest of the group still has lessons.`
                : hasLessons
                  ? `${label} is skipped, but lessons are still placed on it.`
                  : `No lessons are planned for ${label}.`}{" "}
              Removing the skip restores the original lesson dates. You can
              remove it any time before the day passes.
            </p>
          )}
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#687368]"
            >
              Keep skip
            </button>
            {/* The first dialog's scope choice, for the groups still to
                skip -- and the retry after a skip that stopped part-way. */}
            {othersSelectedCount > 0 && (
              <button
                type="button"
                onClick={() => onSkipOthers("selected")}
                disabled={saving}
                className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#28543D] disabled:opacity-50"
              >
                {`Skip ${othersSelectedCount} selected ${othersSelectedCount === 1 ? "student" : "students"}`}
              </button>
            )}
            {others > 0 && (
              <button
                type="button"
                onClick={() => onSkipOthers("group")}
                disabled={saving}
                className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#28543D] disabled:opacity-50"
              >
                {`Skip the other ${others === 1 ? "group" : `${others} groups`}`}
              </button>
            )}
            <PrimaryButton onClick={onRemove} disabled={saving}>
              {saving
                ? "Removing…"
                : skippingGroups.length > 1
                  ? `Remove skip for ${skippingGroups.length} groups`
                  : "Remove skip"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="skip-day-title"
    >
      <div className="w-full max-w-md rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-6 shadow-2xl">
        <h2
          id="skip-day-title"
          className="text-2xl font-extrabold text-[#294B39]"
        >
          Skip this day?
        </h2>
        <p className="mt-2 inline-block rounded-full bg-[#EEF0E3] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-[#28543D]">
          {label}
        </p>
        <p className="mt-3 text-sm leading-6 text-[#687368]">
          {/* The future wording used to promise the shift happened "when the
              date arrives". It does not: `skip_day` records the day *and*
              moves every row dated on or after it in the same call, and the
              current week is already materialized -- so skipping Friday on
              Wednesday re-dates Friday's lessons immediately. The teacher saw
              the roster change under copy that said it would not. Both halves
              of the behaviour are deliberate (the record is what makes the
              scheduler skip the day when it fills a later week), so the copy
              is what had to change. */}
          {isFuture
            ? `No lessons will be assigned on ${label}. Lessons already scheduled for that day or later move one school day later now, and the day shows a "Skipped / No school" tag. Weekends are skipped.`
            : `Lessons scheduled for ${label} will be pushed forward one school day. Weekends are skipped.`}{" "}
          Lesson statuses are not changed and completions are never altered.
        </p>
        <div className="mt-5 grid gap-2">
          <button
            type="button"
            onClick={() => setScope("group")}
            aria-pressed={scope === "group"}
            className="flex items-start justify-between gap-3 rounded-[6px] border p-4 text-left"
            style={{
              backgroundColor: scope === "group" ? "#EEF0E3" : "#fff",
              borderColor: scope === "group" ? "#28543D" : "#DED8CC",
            }}
          >
            <span>
              <span className="block font-bold text-[#294B39]">
                {totalGroups > 1
                  ? leftOut.length
                    ? "Every group that can skip"
                    : "Every group"
                  : "Entire group"}
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#687368]">
                {groupLabel}
                {offPaceGroups.length > 0 &&
                  `. ${offPaceGroups.join(", ")} ${offPaceGroups.length === 1 ? "doesn't" : "don't"} teach on ${label}: only ${offPaceGroups.length === 1 ? "its" : "their"} students with a lesson that day are skipped.`}
                {leftOut.length > 0 &&
                  `. ${leftOut.join(", ")} can't skip ${label} and ${leftOut.length === 1 ? "is" : "are"} left as ${leftOut.length === 1 ? "it is" : "they are"}.`}
              </span>
            </span>
            <span className="shrink-0 rounded-full border border-[#D6CEBF] bg-white px-3 py-1 text-xs font-bold text-[#28543D]">
              {groupCount} {groupCount === 1 ? "student" : "students"}
            </span>
          </button>
          <button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => setScope("selected")}
            aria-pressed={scope === "selected"}
            className="flex items-start justify-between gap-3 rounded-[6px] border p-4 text-left disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: scope === "selected" ? "#EEF0E3" : "#fff",
              borderColor: scope === "selected" ? "#28543D" : "#DED8CC",
            }}
          >
            <span>
              <span className="block font-bold text-[#294B39]">
                Selected students only
              </span>
              <span className="mt-1 block text-xs leading-5 text-[#687368]">
                {selectedCount === 0
                  ? "Select students from the roster first."
                  : "Only the students you selected are pushed forward."}
              </span>
            </span>
            <span className="shrink-0 rounded-full border border-[#D6CEBF] bg-white px-3 py-1 text-xs font-bold text-[#28543D]">
              {selectedCount} {selectedCount === 1 ? "student" : "students"}
            </span>
          </button>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#687368]"
          >
            Cancel
          </button>
          <PrimaryButton onClick={() => onConfirm(scope)} disabled={saving}>
            {saving ? "Saving…" : isFuture ? "Schedule skip" : "Skip day"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const ShiftScheduleModal = ({
  direction,
  count,
  saving,
  onConfirm,
  onClose,
}: {
  direction: "back" | "forward";
  count: number;
  saving: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
    role="dialog"
    aria-modal="true"
    aria-labelledby="shift-schedule-title"
  >
    <div className="w-full max-w-md rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-6 shadow-2xl">
      <h2
        id="shift-schedule-title"
        className="text-2xl font-extrabold text-[#294B39]"
      >
        Move {count}{" "}
        {count === 1 ? "student's schedule" : "students' schedules"} 1 day{" "}
        {direction}?
      </h2>
      <p className="mt-3 text-sm leading-6 text-[#687368]">
        Each student&apos;s schedule shifts one school day{" "}
        {direction === "forward" ? "later" : "earlier"} relative to their own
        position. No common target lesson is set. Weekends are skipped
        {direction === "forward"
          ? " (a shift from Friday lands on Monday)"
          : ""}
        .
      </p>
      <p className="mt-2 text-sm leading-6 text-[#687368]">
        Lesson statuses are untouched. Nothing is marked Skipped and completions
        are never altered. Students in a live session are unaffected until they
        next open a lesson.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#687368]"
        >
          Cancel
        </button>
        <PrimaryButton onClick={onConfirm} disabled={saving}>
          {saving ? "Moving…" : "Confirm"}
        </PrimaryButton>
      </div>
    </div>
  </div>
);

/** A failed save, inside the dialog that made it (R32). The page banner's red,
 *  without its dismiss: the dialog's primary button reads "Try again" while
 *  this shows, and that is the way past it. */
const DialogError = ({ message }: { message: string }) => (
  <div
    role="alert"
    className="rounded-[6px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
  >
    {message}
  </div>
);

const LessonSequencingPageContent = ({
  accessibleAccounts,
  canEditSequencing,
}: {
  accessibleAccounts: AccessibleAccount[];
  // Decided by the wrapper at the bottom of this file from its one accounts
  // lookup (R31), not by a second lookup here that could disagree with it.
  canEditSequencing: boolean;
}) => {
  const pacingModeEnabled = usePacingModeEnabled();
  const actingTeacherId = useAuthStore((state) => state.actingTeacherId);
  const selectedAccount = actingTeacherId
    ? accessibleAccounts.find(
        (account) => account.teacher_id === actingTeacherId,
      )
    : accessibleAccounts.find((account) => account.is_you);
  const [searchParams, setSearchParams] = useSearchParams();
  const showNotification = useNotificationStore(
    (state) => state.showNotification,
  );
  const [overview, setOverview] = useState<Overview | null>(null);
  // Every recorded change to this group, newest first -- the export's audit
  // banner shows the latest and counts the rest.
  const [auditLog, setAuditLog] = useState<SequencingEvent[]>([]);
  // Bumped by `refresh`, so the banner picks up the action that just fired
  // rather than describing the state before it.
  const [refreshToken, setRefreshToken] = useState(0);
  const [details, setDetails] = useState<Detail[] | null>(null);
  // Every classroom's roster, independent of the group filter above. The
  // approval review is teacher-wide -- it gates one teacher-wide switch, and
  // the design lists every group's students together -- so it cannot read
  // `details`, which holds only the groups currently on screen.
  const [reviewDetails, setReviewDetails] = useState<Detail[] | null>(null);
  // Distinguishes a failed roster load from one that has not arrived yet.
  // Both leave `reviewDetails` null, and they need different screens.
  const [reviewLoadFailed, setReviewLoadFailed] = useState(false);
  const reviewRequestRef = useRef(0);
  const [settingsClassroomId, setSettingsClassroomId] = useState<number | null>(
    Number(searchParams.get("classroomId")) || null,
  );
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [dayOffset, setDayOffset] = useState(() =>
    clampDayOffset(Number(searchParams.get("dayOffset"))),
  );
  const [search, setSearch] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(
    new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(false);
  // The roster on screen is no longer known to be current: the last fetch
  // failed and `details` deliberately still holds the previous day, so the
  // teacher keeps something to read. Without this the stale grid looks
  // authoritative and the error banner sits far enough away to miss.
  const [rosterStale, setRosterStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Set while the review is the starting-point step of a switch into or out
  // of No expectation mode rather than an activation. `pace` is the dialog's
  // draft, held here because closing the dialog re-seeds it from the server:
  // saved first when leaving the mode, so the new pace plans from the picks.
  const [modeSwitch, setModeSwitch] = useState<{
    target: AssignmentMode;
    pace: Record<string, unknown> | null;
  } | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);
  // What the week becomes if this pick is saved. Fetched, never derived here:
  // the server runs the same window computation the save will, so a preview
  // built locally could promise a week Save would not produce.
  const [weekPreview, setWeekPreview] = useState<WeekPreview | null>(null);
  /** Does the preview cover more than the day being edited?
   *
   *  A daily pick governs the days after it too, so the list can span several.
   *  A weekly classroom sends `day: null` on every row and is one undated
   *  batch, which collapses to a single distinct value here and keeps the
   *  original copy. Counting distinct days rather than trusting `unit`, so a
   *  daily classroom whose horizon happens to be one day still reads as one.
   */
  const previewSpansDays = useMemo(() => {
    const days = new Set(
      (weekPreview?.lessons ?? [])
        .map((lesson) => lesson.day)
        .filter((day): day is string => Boolean(day)),
    );
    return days.size > 1;
  }, [weekPreview]);
  /** Why the week preview is missing, which decides what the modal may promise.
   *
   *  "rejected" is the server refusing this pick (422): the same check runs on
   *  save, so the save cannot succeed and Save is held closed. "unavailable" is
   *  anything else -- a dropped request, a 5xx -- where the pick is still valid
   *  and only the list is missing. A single boolean could not tell them apart,
   *  and the one message it carried told teachers "Saving still works" in
   *  exactly the case where saving returns the identical 422.
   */
  const [previewError, setPreviewError] = useState<
    null | "rejected" | "unavailable"
  >(null);
  // Per-classroom request shape, read inside `loadRoster`. A ref keeps it
  // current there without re-creating the loader and re-triggering every
  // effect that depends on it.
  const weeklyRosterModesRef = useRef<Map<number, boolean>>(new Map());
  const [changeConfirming, setChangeConfirming] = useState(false);
  // A save that failed from inside a dialog, shown in that dialog (R32). The
  // page's `error` banner renders in the page flow, under every dialog's
  // overlay, so a refused mode switch or Change lesson read as nothing at all.
  const [dialogError, setDialogError] = useState<string | null>(null);
  // Which dialog is open, read when a save fails. The teacher can close a
  // dialog while its save is in flight, and a failure landing after that
  // belongs on the page rather than in a dialog nobody can see.
  const openDialogRef = useRef<Dialog | null>(null);
  openDialogRef.current = settingsOpen
    ? "settings"
    : reviewOpen
      ? "review"
      : changeOpen
        ? "change"
        : null;
  // Opening, closing or stepping a dialog starts it clean; a failure stays
  // only while the dialog that made it is still on screen.
  useEffect(() => {
    setDialogError(null);
  }, [settingsOpen, reviewOpen, changeOpen, changeConfirming]);
  /** Report a failed save made from `dialog`: inside it while it is still
   *  open, which keeps the teacher's draft in front of them with the retry,
   *  and on the page once it is not. Never neither. */
  const reportDialogError = (dialog: Dialog, message: string) => {
    if (openDialogRef.current === dialog) setDialogError(message);
    else setError(message);
  };
  // The day header a teacher clicked, and the pending bulk shift.
  const [skipTarget, setSkipTarget] = useState<{
    date: string;
    isFuture: boolean;
  } | null>(null);
  const [shiftConfirm, setShiftConfirm] = useState<"back" | "forward" | null>(
    null,
  );
  const [daily, setDaily] = useState(1);
  // Lessons per week, weekly mode only. Stored in its own column since 0072 --
  // it used to ride on `expected_days_per_week`, which also decides which
  // weekdays serve, so raising the pace on a Wednesday quietly declared that
  // only Monday and Tuesday do.
  const [weekly, setWeekly] = useState(MAX_DAYS_PER_WEEK);
  const [mode, setMode] = useState<Mode>("completion");
  // The batch-or-rate choice, held locally while the modal is open. Stored
  // rather than inferred from `daily`: a weekly batch is saved as one lesson
  // a day across five days, which is a pace a daily group can also hold, so
  // the two are indistinguishable once the choice is thrown away.
  const [assignmentMode, setAssignmentMode] =
    useState<AssignmentMode>("weekly");
  const [selections, setSelections] = useState<Record<string, number>>({});
  // Which rows the teacher actually edited, sent to `activate` as `re_picked`.
  //
  // The seeding effect below fills every blank row from
  // `suggested_curriculum_node_id`, and `change_lesson` never moves that
  // column, so the posted node alone cannot say whether a teacher chose a
  // lesson or the form echoed the seed back. Activation used to re-place every
  // student it was sent, which reverted a Change lesson made seconds earlier.
  // The backend compares the node to the suggestion when this is absent; the
  // one case no comparison can reach is picking another lesson and then
  // returning to the suggested one, which is what this records.
  //
  // Lives exactly as long as `selections` does, and deliberately: closing the
  // modal keeps the teacher's choices, so clearing the marks on reopen would
  // keep the choice and throw away the fact that it was one -- losing the
  // single case this exists for. Cleared per classroom as each activation
  // commits, below.
  const [reviewEdited, setReviewEdited] = useState<Set<string>>(new Set());
  const [reviewCommitted, setReviewCommitted] = useState<
    Record<string, number>
  >({});
  const [reviewedAll, setReviewedAll] = useState(false);
  // The design's review modal filters, bulk-applies and selects rows.
  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewGroup, setReviewGroup] = useState("All groups");
  const [reviewSelected, setReviewSelected] = useState<Set<string>>(new Set());
  const [bulkNode, setBulkNode] = useState<number | null>(null);
  // Lets the teacher narrow the table to the students actually blocking
  // activation. With a full roster the gate is otherwise a needle hunt.
  const [reviewOnlyUnplaced, setReviewOnlyUnplaced] = useState(false);
  const [targetNode, setTargetNode] = useState<number | null>(null);
  const [disposition, setDisposition] = useState<"push_incomplete" | "skip">(
    "push_incomplete",
  );

  // Paging fires a refetch per click; only the newest response may win.
  const rosterRequestRef = useRef(0);
  // Whether the newest overview is No expectation mode, read inside
  // `loadDetails`. Set from the response rather than from render: `refresh`
  // reads the roster straight after the overview, before a render has caught
  // `pointerMode` up, so the read that follows a switch into the mode would
  // otherwise page to whatever day the teacher had open.
  const pointerModeRef = useRef(false);

  // Scope comes from the dropdown alone now the roster-view toggle is gone.
  // loadOverview always seeds groupIds, so this fallback only covers the window
  // before the first response -- an all-classrooms default here would be dead
  // code. The landing scope is deliberately unchanged: one group, as it was
  // when `view` defaulted to "group".
  const classroomIds = useMemo(() => {
    if (!overview) return [];
    if (groupIds.length) return groupIds;
    return settingsClassroomId ? [settingsClassroomId] : [];
  }, [overview, groupIds, settingsClassroomId]);

  //
  // Read off the overview, not off `primary`. `primary` is part of the roster
  // response, so keying the request on it asks the roster which shape to
  // request in the reply it has not sent yet: the first load always guessed,
  // and nothing re-requested once the answer arrived. `isWeeklyAssignment`
  // defaults to weekly, so the guess was wrong for exactly the daily
  // classrooms -- weekly windows under a daily roster.
  // Every selected classroom's mode, as one stable key. The refetch has to
  // notice a change in any of them, not just the first -- and the request
  // shape is now chosen per classroom inside `loadDetails`.
  const rosterModeKey = (overview?.classrooms ?? [])
    .filter((classroom) => classroomIds.includes(classroom.id))
    .map(
      (classroom) =>
        `${classroom.id}:${classroom.settings.assignment_mode ?? overview?.teacher_settings.assignment_mode ?? "weekly"}`,
    )
    .join(",");

  // The column headers are shared across the whole table, so they can only
  // describe one mode. A mixed selection therefore heads a daily group with
  // week ranges or the reverse -- the request shape is right per classroom, the
  // heading is not. Left as the primary's mode rather than guessed at, because
  // the fix is a design question: either group the table by classroom or stop
  // allowing mixed selections.
  const weeklyRoster = isWeeklyAssignment(
    (overview?.classrooms ?? []).find(
      (classroom) => classroom.id === (classroomIds[0] ?? -1),
    )?.settings.assignment_mode ?? overview?.teacher_settings.assignment_mode,
  );
  // No expectation mode is teacher-wide (O4), so one answer for the page: no
  // dates, no days, a roster of Previous / Current / Next lessons, and moves
  // that step a lesson rather than a day.
  const pointerMode = isPointerMode(overview?.teacher_settings.assignment_mode);
  const pointerReview = pointerMode && !modeSwitch;
  weeklyRosterModesRef.current = new Map(
    (overview?.classrooms ?? []).map((classroom) => [
      classroom.id,
      isWeeklyAssignment(
        classroom.settings.assignment_mode ??
          overview?.teacher_settings.assignment_mode,
      ),
    ]),
  );

  /** @returns whether the page now holds a fresh overview. */
  const loadOverview = async (): Promise<boolean> => {
    try {
      setError(null);
      const response = await api.get<Overview>("/lesson-sequencing");
      pointerModeRef.current = isPointerMode(
        response.data.teacher_settings.assignment_mode,
      );
      setOverview(response.data);
      const known = response.data.classrooms.map((item) => item.id);
      const nextId =
        settingsClassroomId && known.includes(settingsClassroomId)
          ? settingsClassroomId
          : (known[0] ?? null);
      setSettingsClassroomId(nextId);
      setGroupIds((current) => {
        const kept = current.filter((id) => known.includes(id));
        return kept.length ? kept : nextId ? [nextId] : [];
      });
      return true;
    } catch (loadError) {
      setError(getError(loadError, "Failed to load lesson sequencing."));
      return false;
    } finally {
      setLoading(false);
    }
  };

  // One fetch per classroom in scope. There is no aggregate endpoint, and the
  // change-lesson modal needs each classroom's own lesson_options anyway.
  /** @returns whether the roster on screen is now this call's own fresh data. */
  const loadDetails = async (
    ids: number[],
    offset: number,
  ): Promise<boolean> => {
    if (!ids.length) {
      setDetails([]);
      return true;
    }
    const requestId = rosterRequestRef.current + 1;
    rosterRequestRef.current = requestId;
    try {
      setRosterLoading(true);
      const responses = await Promise.all(
        ids.map((id) =>
          api.get<Detail>(`/classrooms/${id}/lesson-sequencing`, {
            // Per classroom, not per request. `assignment_mode` is a
            // per-classroom override, so a mixed selection asked one shape for
            // all of them and rendered at least one group against settings it
            // does not have.
            //
            // Weekly columns follow the student's position, so that view does
            // not page and the API rejects an offset alongside it.
            //
            // No expectation mode has no days to page either (R29). The
            // server ignores the offset there but echoes it back, and the page
            // reads a non-zero echo as a day other than today.
            params: weeklyRosterModesRef.current.get(id)
              ? { weekly_view: true }
              : { day_offset: pointerModeRef.current ? 0 : offset },
          }),
        ),
      );
      // Superseded, so this call did not put anything on screen and must not
      // report that it did -- the request that overtook it owns what the
      // teacher ends up seeing, including any message about it.
      if (rosterRequestRef.current !== requestId) return false;
      setDetails(responses.map((response) => response.data));
      setRosterStale(false);
      return true;
    } catch (loadError) {
      if (rosterRequestRef.current !== requestId) return false;
      setRosterStale(true);
      setError(getError(loadError, "Failed to load classroom sequencing."));
      return false;
    } finally {
      if (rosterRequestRef.current === requestId) setRosterLoading(false);
    }
  };

  useEffect(() => {
    void loadOverview();
  }, []);

  // The header card, settings modal and activation review all act on this
  // classroom, so it must be one we actually fetched. Derived during render
  // rather than corrected in an effect: an effect leaves one committed frame
  // where the selector names group B while `primary` is group A.
  const effectiveSettingsId = useMemo(() => {
    if (settingsClassroomId && classroomIds.includes(settingsClassroomId)) {
      return settingsClassroomId;
    }
    return classroomIds[0] ?? null;
  }, [settingsClassroomId, classroomIds]);

  // The audit banner. Every shift, skip, scheduled skip and removal is recorded
  // server-side; this surfaces the newest and counts the rest.
  //
  // Everyone with access sees it except whoever made the change. The rule is
  // symmetric and not about roles: if a co-teacher toggles sequencing the
  // teacher is told, and the other way round.
  //
  // The exclusion is the endpoint's, not this component's. It has to happen
  // before the 200-row cap `events()` applies, or the viewer's own rows fill
  // the page and hide older changes by anyone else -- a bulk action writes one
  // event per student, so a class of thirty reaches that in a handful of them.
  //
  // Its own effect rather than riding along with `loadDetails`, so the
  // dependency list can be honest.
  useEffect(() => {
    const focus = effectiveSettingsId;
    // Cleared before the request, not only on failure: switching groups
    // otherwise left the previous group's entry on screen until the new fetch
    // returned, or indefinitely if it hung. A banner naming the wrong group is
    // worse than no banner.
    setAuditLog([]);
    if (!focus) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<SequencingEvent[]>(
          `/classrooms/${focus}/lesson-sequencing/events`,
        );
        if (!cancelled) setAuditLog(response.data);
      } catch {
        // Advisory only. A failed event fetch must not take the roster with it.
        if (!cancelled) setAuditLog([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveSettingsId, refreshToken]);

  useEffect(() => {
    void loadDetails(classroomIds, dayOffset);
    // Keyed on every selected classroom's mode, so switching any of them
    // between branches re-asks -- the shape is decided per classroom.
  }, [classroomIds, dayOffset, rosterModeKey]);

  /**
   * Load the roster the activation review approves against.
   *
   * Always at day_offset 0: a starting point is a position in the sequence, not
   * a position in the three-day window the roster happens to be paged to.
   *
   * A failure used to leave `reviewDetails` null and say nothing, on the
   * reasoning that the gate would stay closed until a reload. That was tolerable
   * while the review was hard to reach. It is not now the switch always opens
   * it: null also means "still loading", so a failed fetch renders as an empty
   * roster -- and the empty-roster copy invites the teacher to turn it on while
   * the button it points at is disabled. The failure is tracked separately so
   * the modal can say what happened and offer the retry.
   *
   * `reviewRequestRef` drops stale responses, which a plain cancelled flag could
   * not do once this became callable from a Retry button as well as an effect.
   */
  const loadReviewDetails = useCallback(async () => {
    const ids = overview?.classrooms.map((classroom) => classroom.id) ?? [];
    const request = ++reviewRequestRef.current;
    setReviewLoadFailed(false);
    if (!ids.length) {
      setReviewDetails([]);
      return;
    }
    try {
      const responses = await Promise.all(
        ids.map((id) =>
          api.get<Detail>(`/classrooms/${id}/lesson-sequencing`, {
            params: { day_offset: 0 },
          }),
        ),
      );
      if (request !== reviewRequestRef.current) return;
      setReviewDetails(responses.map((item) => item.data));
    } catch {
      if (request !== reviewRequestRef.current) return;
      setReviewDetails(null);
      setReviewLoadFailed(true);
    }
  }, [overview]);

  useEffect(() => {
    void loadReviewDetails();
  }, [loadReviewDetails]);

  // Selection survives paging days — it is keyed by student and the roster is
  // the same people either way. It cannot survive a change of which groups are
  // on screen, because those students are no longer in the list.
  useEffect(() => {
    setSelectedStudents(new Set());
  }, [classroomIds]);

  // Replace rather than push: switching group or paging a day is not a
  // navigation step, and pushing made Back walk the teacher's own selections
  // instead of leaving the page.
  useEffect(() => {
    const next: Record<string, string> = {};
    if (effectiveSettingsId) next.classroomId = String(effectiveSettingsId);
    if (dayOffset !== 0) next.dayOffset = String(dayOffset);
    // `withAsOf`, because `next` is built from scratch and would otherwise drop
    // QA's simulated date from the URL a moment after the page loads.
    setSearchParams(withAsOf(next), { replace: true });
  }, [effectiveSettingsId, dayOffset]);

  // No days to page in No expectation mode (R29). A `?dayOffset` carried in
  // from a daily roster or a bookmark is dropped -- from the URL too, through
  // the effect above -- rather than kept to resurface the next time the
  // teacher leaves the mode.
  useEffect(() => {
    if (pointerMode) setDayOffset(0);
  }, [pointerMode]);

  // No `?? details[0]` fallback: a genuine miss must render as "not loaded"
  // rather than silently acting on a different group.
  const primary = useMemo(
    () =>
      details?.find((item) => item.classroom_id === effectiveSettingsId) ??
      null,
    [details, effectiveSettingsId],
  );
  // The roster shows today's columns. Always, in No expectation mode: Current
  // is where the student is, not a date, so a roster still holding an offset
  // read before the switch must not take its highlight away.
  const showingToday = pointerMode || primary?.day_offset === 0;

  // One teacher-wide toggle. The spec: "Auto-sequencing is a single
  // teacher-wide toggle, not per group... Groups still exist as organizational
  // containers and are used for viewing, filtering, and bulk actions on the
  // sequencing screen. They do NOT gate sequencing state."
  //
  // So this writes the teacher default and nothing else. The per-group
  // override, the separate "Teacher default" row and the enable-for-all-groups
  // escalation all existed to manage per-group state the spec does not have.
  const teacherEnabled = Boolean(overview?.teacher_settings.enabled);
  // Nothing to schedule from: the binding fan-out clears each group's
  // sequence when the organization loses the curriculum, so the switch would
  // otherwise stay throwable with nothing behind it.
  const curriculumAvailable = Boolean(overview?.curriculum_available);

  // Whether the roster has anything to be a roster of. Both halves matter and
  // neither is enough: the switch being on used to be the only gate, so a
  // teacher whose organization lost its curriculum got the full grid --
  // group picker, day columns, "Curriculum setup required" -- over a schedule
  // that cannot exist. The empty panel is the honest shape for both, and
  // deriving it once keeps the panel and the group picker above it from
  // disagreeing about which one to draw.
  const sequencingActive = teacherEnabled && curriculumAvailable;

  // What the dialog header and the save toast call the scope. Lesson settings
  // are teacher-wide, so the only question is whether "all groups" reads
  // oddly to a teacher who has one.
  const settingsScopeLabel = useMemo(() => {
    const classrooms = overview?.classrooms ?? [];
    if (classrooms.length === 1) return classrooms[0].name;
    return "all groups";
  }, [overview]);

  // Settings inputs mirror the classroom the header card acts on. `settings`
  // is a fresh object on every fetch, so this would otherwise re-run on each
  // refetch and pager click — silently reverting unsaved edits while the modal
  // is open. Only sync when it is closed.
  //
  // Except when the teacher switches group *from inside* the modal: with
  // auto-sequencing off the group selector lives here, so `primary` changes
  // while it is open. Skipping the sync then left the previous group's pace on
  // screen and saved it onto the newly selected group.
  useEffect(() => {
    // Only while the dialog is closed: `settings` is a fresh object on every
    // fetch, so this would otherwise re-run on each refetch and pager click,
    // silently reverting unsaved edits with the modal open.
    if (settingsOpen) return;
    const seed = settingsFormSeed(overview?.teacher_settings);
    if (!seed) return;
    setDaily(seed.daily);
    setWeekly(seed.weekly);
    setMode(seed.mode as Mode);
    setAssignmentMode(seed.assignmentMode as AssignmentMode);
  }, [overview?.teacher_settings, settingsOpen]);

  const reviewRoster = useMemo<RosterStudent[]>(
    () =>
      (reviewDetails ?? []).flatMap((item) =>
        item.students.map((student) => ({
          ...student,
          classroom_id: item.classroom_id,
          classroom_name: item.classroom_name,
        })),
      ),
    [reviewDetails],
  );

  // Lesson options are per classroom, so the picker in each row has to read
  // its own student's group rather than one shared list.
  // Entering No expectation mode, without drafts: the switch refuses a pick of
  // an unpublished lesson, which the walk would step straight over.
  const reviewOptionsByClassroom = useMemo(() => {
    const map = new Map<number, LessonOption[]>();
    const withoutDrafts = pointerReview || isPointerMode(modeSwitch?.target);
    (reviewDetails ?? []).forEach((item) =>
      map.set(
        item.classroom_id,
        withoutDrafts
          ? item.lesson_options.filter((option) => !option.is_draft)
          : item.lesson_options,
      ),
    );
    return map;
  }, [reviewDetails, modeSwitch?.target, pointerReview]);

  // Seed each row from the starting point the backend already worked out.
  //
  // This is what makes the gate satisfiable at all. `derive_default_placements`
  // computes a suggestion and `materialize_pending_sequences` stores it, and the
  // API has been returning it as `suggested_curriculum_node_id` all along --
  // nothing here read it, so every row opened empty, every student counted as
  // unplaced, and "Approve and turn on" could never enable until the teacher
  // hand-picked a lesson for all of them. Only fills blanks, so a teacher's own
  // choice is never overwritten by a refetch.
  //
  // `seededRoster` records which roster this has filled. The roster arrives one
  // render before the effect runs, and a switch approved in that render would
  // post no picks -- every student left waiting for placement -- so a switch
  // can be approved only once the roster on screen has been seeded.
  const [seededRoster, setSeededRoster] = useState<RosterStudent[] | null>(
    null,
  );
  useEffect(() => {
    setSeededRoster(reviewRoster);
    if (!reviewRoster.length) return;
    setSelections((current) => {
      let changed = false;
      const next = { ...current };
      reviewRoster.forEach((student) => {
        // A mode switch starts each student from where they are when leaving
        // No expectation mode, and from the server's suggestion entering it.
        const suggested = reviewSuggestion(
          student,
          modeSwitch?.target,
          pointerReview,
        );
        const key = rowKey(student);
        if (next[key] === undefined && suggested) {
          next[key] = suggested;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [reviewRoster, modeSwitch, pointerReview]);

  const reviewSeeded = seededRoster === reviewRoster;

  // A closed review is no longer a switch, whichever way it closed.
  useEffect(() => {
    if (!reviewOpen) setModeSwitch(null);
  }, [reviewOpen]);

  // The sign-off opens from the Auto-sequencing switch and nowhere else -- see
  // `toggleAutoSequencing`. An effect used to open it once per page load as
  // well, "ungated on purpose, for now", so a teacher arriving to read the
  // roster was handed a modal they had not asked for and could only cancel.
  // That is what made the review read as noise rather than as the gate the
  // design intends: turning the switch on *is* the moment a starting lesson
  // needs approving.

  const rosterStudents = useMemo<RosterStudent[]>(
    () =>
      (details ?? []).flatMap((item) =>
        item.students.map((student) => ({
          ...student,
          classroom_id: item.classroom_id,
          classroom_name: item.classroom_name,
        })),
      ),
    [details],
  );

  const visibleStudents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rosterStudents;
    return rosterStudents.filter((student) =>
      student.name.toLowerCase().includes(query),
    );
  }, [rosterStudents, search]);

  // The groups the rows on screen actually came from, not the ones requested.
  // `classroomIds` moves the moment the multi-select changes while `details`
  // still holds the previous fetch, so a label read off the request could name
  // a group whose students are not listed yet -- or say "All groups" over one
  // group's roster.
  const displayedClassroomIds = useMemo(
    () => new Set((details ?? []).map((item) => item.classroom_id)),
    [details],
  );

  const detailsById = useMemo(() => {
    const map = new Map<number, Detail>();
    (details ?? []).forEach((item) => map.set(item.classroom_id, item));
    return map;
  }, [details]);

  const changeTargetClassroomId = useMemo(
    () => changeTargetClassroom(rosterStudents, selectedStudents),
    [rosterStudents, selectedStudents],
  );

  // Who each bulk action reaches, derived once so a handler and the dialog
  // counting for it read the same list. The selection's group is where Change
  // lesson and Move a day go; Skip day asks the same question of every group
  // on screen (`skipWrites`).
  const selectionStudentIds = useMemo(
    () =>
      selectedStudentIdsIn(
        rosterStudents,
        selectedStudents,
        changeTargetClassroomId,
      ),
    [rosterStudents, selectedStudents, changeTargetClassroomId],
  );

  // No drafts in No expectation mode: the move refuses an unpublished lesson,
  // which the student's walk would step straight over.
  const changeLessonOptions = (
    changeTargetClassroomId
      ? (detailsById.get(changeTargetClassroomId)?.lesson_options ?? [])
      : []
  ).filter((option) => !(pointerMode && option.is_draft));

  // The pick, resolved against the list the picker actually shows.
  //
  // `lesson_options` holds one entry per lesson, collapsing a lesson a legacy
  // curriculum placed at two slots down to the earliest. Placement does not
  // follow that rule, so the preselection -- the student's current lesson --
  // can name the copy that lost, and matching by node alone returned null: the
  // picker showed its placeholder over a real selection, the modal previewed a
  // lesson nobody had chosen, and Save was armed on a node the server refused.
  //
  // Falling back to the lesson finds the entry that stands for it. Only while
  // `targetNode` is still the preselection -- once the teacher picks, the node
  // is one of these entries and matches directly, and a `targetNode` left over
  // from a previous selection must not resolve to this one's lesson.
  //
  // This resolves what the picker *shows* and whether the pick is savable at
  // all. It does not change what is sent: the preview and the write both carry
  // `targetNode`, so confirming an untouched preselection leaves the student on
  // the node they were already on.
  const preselectedNode = sharedCurrentLesson(rosterStudents, selectedStudents);
  const preselectedLessonId = sharedCurrentLessonId(
    rosterStudents,
    selectedStudents,
  );
  const selectedOption =
    changeLessonOptions.find(
      (option) => option.curriculum_node_id === targetNode,
    ) ??
    (targetNode !== null &&
    targetNode === preselectedNode &&
    preselectedLessonId !== null
      ? (changeLessonOptions.find(
          (option) => option.lesson_id === preselectedLessonId,
        ) ?? null)
      : null);

  // The week the pick produces. Single selection only -- the window is per
  // student, and a bulk change would need one preview each, which is a list the
  // design does not have a place for.
  const previewStudentId =
    selectedStudents.size === 1 ? [...selectedStudents][0] : null;
  useEffect(() => {
    // Cleared before every exit, not just before a fetch. The guards below
    // return for an unplaced student and for a mixed selection with no shared
    // current lesson, and clearing only on the fetch path left the previous
    // student's batch on screen under an empty picker until a lesson was
    // chosen.
    setWeekPreview(null);
    setPreviewError(null);
    // `selectedOption`, not `targetNode`. Opening the modal preselects the
    // student's current lesson, and a node the option list does not carry
    // resolves to no option -- the picker shows its placeholder while the raw
    // node is still set. Keyed on the node, this fetched a lesson the teacher
    // had not chosen and could not see, and the 422 came back as "Could not
    // load this week's lessons" over an untouched form.
    if (
      !changeOpen ||
      !changeTargetClassroomId ||
      !selectedOption ||
      targetNode === null ||
      previewStudentId === null
    ) {
      return;
    }
    let cancelled = false;
    // `cancelled` stops a late response overwriting a newer one; the clear
    // above is what stops the *old* answer being shown while the new one is
    // data still on screen: on a slow request the modal kept showing the
    // previous student's batch under the newly picked lesson, and Save was
    // reachable from there.
    setWeekPreview(null);
    void api
      .get<WeekPreview>(
        `/classrooms/${changeTargetClassroomId}/lesson-sequencing/change-lesson/preview`,
        {
          params: {
            student_id: previewStudentId,
            // `targetNode`, not the option it resolved to. For an untouched
            // preselection those differ -- the student sits on the later copy
            // of a duplicated lesson and the picker offers the earlier one --
            // and sending the option's node would preview the week starting at
            // a slot the student is already past. The endpoints accept either
            // copy, so the pick keeps the node it names and the preview
            // matches what Save writes.
            curriculum_node_id: targetNode,
            // The offset the *loaded* roster represents, which is what Save
            // sends. `dayOffset` moves the moment a pager is clicked while
            // `primary` still describes the previous page, so a preview keyed
            // to it would promise a different starting day from the write --
            // and the Change button stays enabled while the roster is merely
            // dimmed.
            day_offset: primary?.day_offset ?? 0,
            // The checkbox below, as Save sends it. Moving back, a current
            // lesson kept finishable follows the pick and a skipped one does
            // not, so the week depends on it.
            disposition,
          },
        },
      )
      .then((response) => {
        if (!cancelled) setWeekPreview(response.data);
      })
      .catch((error: unknown) => {
        // A 422 is the server rejecting this lesson, and `change-lesson` runs
        // the same check -- so it blocks Save rather than degrading. Every
        // other failure leaves the pick valid and only costs the list, which
        // the server rebuilds on save.
        if (cancelled) return;
        setWeekPreview(null);
        const status =
          typeof error === "object" &&
          error !== null &&
          "response" in error &&
          typeof (error as { response?: { status?: unknown } }).response
            ?.status === "number"
            ? (error as { response: { status: number } }).response.status
            : null;
        setPreviewError(status === 422 ? "rejected" : "unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [
    changeOpen,
    changeTargetClassroomId,
    selectedOption,
    targetNode,
    previewStudentId,
    // The preview starts at the day being edited, so paging the roster to
    // another day asks a different question -- keyed to the loaded page, not
    // the requested one, for the same reason the request is.
    primary?.day_offset,
    disposition,
  ]);

  // Only meaningful for a single selection, which is when the design shows it.
  const changeStudent =
    selectedStudents.size === 1
      ? (rosterStudents.find((student) =>
          selectedStudents.has(student.student_id),
        ) ?? null)
      : null;

  const selectionSpansGroups =
    selectedStudents.size > 0 && changeTargetClassroomId === null;

  /** Who the pending change touches, and what it does to each of them.
   *
   *  Built from the roster the page already holds rather than asked for: every
   *  student row carries its own `current` lesson, which is the "old" half of
   *  the comparison the confirmation table shows. */
  const changeImpact = useMemo(
    () =>
      changeLessonImpact(
        rosterStudents,
        selectedStudents,
        changeTargetClassroomId,
        targetNode,
      ),
    [rosterStudents, selectedStudents, changeTargetClassroomId, targetNode],
  );

  // Linear's pacing copy is expressed per week; the stored fields are
  // days × lessons/day. Derive rather than store a third value that could
  // drift out of step with them.
  //
  // Which factor the teacher varies is the whole difference between the two
  // branches: a weekly batch picks the week's total at one lesson a day, and a
  // daily pace picks the cap across all five days.
  const weeklyMode = isWeeklyAssignment(assignmentMode);
  // The dialog's draft is No expectation mode: no pace, so neither pace picker
  // and no Completion/Pacing choice (R1) -- the mode is always completion.
  const draftPointer = isPointerMode(assignmentMode);
  // Offered while the flag is on, and always to a teacher already in it, who
  // has to be able to see the mode they are in and leave it.
  const offerNoExpectation = ENABLE_NO_EXPECTATION_MODE || pointerMode;
  // Saving the draft would move into or out of the mode, which goes through
  // the starting-point review rather than saving here.
  const draftSwitchesMode = isModeSwitch(
    overview?.teacher_settings.assignment_mode,
    assignmentMode,
  );
  const weeklyTotal = weeklyMode
    ? lessonsPerWeek(weekly, 1)
    : lessonsPerWeek(MAX_DAYS_PER_WEEK, daily);

  const canPageBack = visibleStudents.some((student) => student.has_earlier);
  /** Can a one-day shift move anybody in the current selection?
   *
   *  A shift is all-or-nothing per student and backwards is refused whenever
   *  today already holds their work -- the ordinary state -- so the button was
   *  live for a move that could not happen, and the refusal's only trace was a
   *  warning that had to survive a reload to be read. QA reported it as the
   *  action silently doing nothing, twice.
   *
   *  Offered if *any* selected student can move, because the server moves the
   *  ones it can and reports the rest. Absent fields read as true so an older
   *  server, which sends neither, keeps the buttons it always had.
   */
  const shiftableSelection = (key: "can_shift_back" | "can_shift_forward") =>
    rosterStudents.some(
      (student) =>
        selectedStudents.has(student.student_id) && (student[key] ?? true),
    );
  const canShiftBack = shiftableSelection("can_shift_back");
  const canShiftForward = shiftableSelection("can_shift_forward");
  const canPageForward = visibleStudents.some((student) => student.has_later);

  /** Is this classroom weekly? Per classroom, not per page.
   *
   *  `weeklyRoster` describes `classroomIds[0]` alone, but `loadDetails` asks
   *  each classroom for its own shape -- so a mixed selection puts weekly rows
   *  and daily rows in one table. Hiding the pagers off the first classroom's
   *  mode took paging away from the daily group behind it, whose rows carry
   *  real `has_earlier`/`has_later` and do respond to `dayOffset`.
   */
  const isWeeklyClassroom = (classroomId?: number | null) =>
    classroomId == null
      ? weeklyRoster
      : (weeklyRosterModesRef.current.get(classroomId) ?? weeklyRoster);

  // Day controls belong on screen while any visible row can use them. With no
  // rows there is nothing to page, so the page-level mode decides.
  // None in No expectation mode, which has no days to page.
  const anyDailyRoster =
    !pointerMode &&
    (visibleStudents.length
      ? visibleStudents.some(
          (student) => !isWeeklyClassroom(student.classroom_id),
        )
      : !weeklyRoster);

  const allVisibleSelected =
    visibleStudents.length > 0 &&
    visibleStudents.every((student) =>
      selectedStudents.has(student.student_id),
    );

  const toggleAllVisible = () => {
    setSelectedStudents(
      allVisibleSelected
        ? new Set()
        : new Set(visibleStudents.map((student) => student.student_id)),
    );
  };

  const toggleStudent = (studentId: number) => {
    setSelectedStudents((current) => {
      const next = new Set(current);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  /** Reload everything on the page, carrying `result` through the reload.
   *
   *  `loadOverview` opens with `setError(null)`, so an operation result set
   *  before refreshing was cleared before the teacher could read it. Every
   *  partial-outcome warning on this page went through that door and none of
   *  them have ever rendered: the bulk shift's "Moved n of m students", the
   *  change-lesson equivalent, and the undo's refusal. A refused backward
   *  shift shows no toast by design, so its warning was the only feedback
   *  there was -- and QA reported the action as silently doing nothing.
   *
   *  Applied only when both reloads actually landed. They report their own
   *  failures through the same `error` slot, and those outrank a partial
   *  outcome: "2 of 3 students moved" over a roster that failed to reload
   *  describes a screen the teacher is not looking at, and hides the reason it
   *  is stale. The operation's toast still fires either way.
   */
  const refresh = async (result?: string) => {
    const overviewLoaded = await loadOverview();
    const rosterLoaded = await loadDetails(classroomIds, dayOffset);
    setRefreshToken((token) => token + 1);
    if (result && overviewLoaded && rosterLoaded) setError(result);
  };

  /** The groups on screen the skip dialog acts on: the daily ones. A weekly
   *  group hands out a batch with no single date, which is why its own day
   *  controls are hidden, so a skip written to it from a mixed view would
   *  move its schedule through a control it does not offer. */
  const dayGroups = (details ?? []).filter(
    (group) =>
      !isWeeklyAssignment(group.settings.assignment_mode) &&
      !isPointerMode(group.settings.assignment_mode),
  );

  /** Does this group's roster put `date` in the column the header heads?
   *
   *  Rows render from each group's own three columns while the headers carry
   *  the primary's dates, and near midnight two organizations' timezones can
   *  put them a day apart. A header then sits over another day's lessons for
   *  that group, so neither its count nor its skip may speak for it. */
  const showsInSameColumn = (group: Detail, date: string) => {
    const column =
      primary?.previous_date === date
        ? "previous_date"
        : primary?.current_date === date
          ? "current_date"
          : primary?.next_date === date
            ? "next_date"
            : null;
    return !column || group[column] === date;
  };

  /** The daily groups a skip on `date` reaches. A slower group whose week
   *  ends before that weekday never teaches it, yet a skip recorded there
   *  still shifts its later rows, so it is left out of the writes and the
   *  counts alike -- unless the day holds one of its lessons anyway (a
   *  starting lesson kept on it), which the skip has to move, or it already
   *  skips the day, which Remove skip has to reach. */
  const skipGroups = (date?: string | null) =>
    dayGroups.filter((group) => {
      if (!date) return true;
      if (!showsInSameColumn(group, date)) return false;
      // Each group's own today: the day can have passed for one group and
      // not another, and the API refuses it there. No roster yet, no today,
      // and nothing to skip.
      const today = groupToday(group);
      if (!today || date < today) return false;
      return (
        servesWeekday(group.settings.expected_days_per_week, date) ||
        studentsWithMovableLessonsOn(group, date).length > 0 ||
        Boolean(group.skipped_dates?.includes(date))
      );
    });

  /** The daily groups a date's header speaks for: every group that teaches
   *  the weekday, shows any lesson on it, or skips it. Not `skipGroups`,
   *  which is the narrower set a skip can write to -- a finished lesson a skip
   *  would not move is still in the column, and a past day still carries its
   *  tag. */
  const headerGroups = (date?: string | null) =>
    dayGroups.filter(
      (group) =>
        !date ||
        (showsInSameColumn(group, date) &&
          (servesWeekday(group.settings.expected_days_per_week, date) ||
            holdsLessonsOn(group, date) ||
            Boolean(group.skipped_dates?.includes(date)))),
    );

  /** The groups on screen that already skip a date, for the skip dialog.
   *
   *  Every group on screen, as the header counts them -- not `primary`, the
   *  group in the URL. With "All groups" chosen the dialog read Adam's days
   *  alone: it told the teacher no lessons were planned for a day the other
   *  groups were teaching, and Remove skip would have lifted Adam's skip only
   *  (GDS Group B, 2026-10-01). */
  const groupsSkipping = (date: string) =>
    skipGroups(date).filter((group) => group.skipped_dates?.includes(date));

  /** Where a skip is written: every group on screen, or, for "Selected
   *  students only", the groups holding a selected student and just those
   *  students in each.
   *
   *  A group whose pace stops before the weekday is here only for the
   *  lessons kept on it, so it is skipped for those students alone: a
   *  group-scope skip shifts every student's later rows, and a classmate with
   *  nothing that day would lose a day for it. */
  const skipWrites = (scope: "group" | "selected", date: string) =>
    skipGroups(date)
      .map((group) =>
        servesWeekday(group.settings.expected_days_per_week, date)
          ? {
              classroomId: group.classroom_id,
              scope,
              studentIds:
                scope === "selected"
                  ? selectedStudentIdsIn(
                      rosterStudents,
                      selectedStudents,
                      group.classroom_id,
                    )
                  : [],
            }
          : {
              classroomId: group.classroom_id,
              scope: "selected" as const,
              studentIds: studentsWithMovableLessonsOn(group, date).filter(
                (studentId) =>
                  scope === "group" || selectedStudents.has(studentId),
              ),
            },
      )
      .filter(
        (write) => write.scope === "group" || write.studentIds.length > 0,
      );

  /** How many of the groups on screen skip a date, for the column headers. */
  const headerSkip = (date?: string | null) =>
    // A weekly header is a week range, and a skip is a day: no day-level tag.
    weeklyRoster
      ? undefined
      : // The daily groups, the set the dialog acts on: a weekly group has no
        // day-level column or control, so its absence from a skip is not "the
        // other groups still teach that day".
        skipCoverage(headerGroups(date), date);

  /** Daily groups, today or later, matching `dayTabProps` in the design.
   *
   *  Two gates, for two reasons. The date half also matches the API, which
   *  refuses a past date -- a header that can only open a failing dialog is
   *  worse than one that does not respond. The mode half is the design's call,
   *  copied from the prototype's `clickable = dailyMode && columnOffset >= 0`:
   *  a weekly batch is handed out as a week rather than pinned to days, so
   *  there is no single day to skip.
   *
   *  Note this leaves Skip day unreachable for a group on the default weekly
   *  setting until it is switched to daily -- in the prototype as well as here.
   *  That is a question for the design, not something to quietly widen in code:
   *  the API would accept it (`skip_day` never checks the mode), so removing
   *  the gate is a one-line change once someone decides a weekly group should
   *  be able to skip a day. */
  const canSkipDate = (date?: string | null) =>
    Boolean(
      date &&
      canEditSequencing &&
      // The saved mode, not `weeklyMode` -- that tracks the settings
      // modal's draft, so an unsaved toggle would arm the header behind it.
      !isWeeklyAssignment(primary?.settings.assignment_mode) &&
      // Skip day does not exist in No expectation mode (R29): nothing is
      // dated, so there is no day to take out.
      !isPointerMode(primary?.settings.assignment_mode) &&
      // A skip writes to every group on screen, and while the roster reloads
      // or failed to, `details` still holds the groups before the change.
      !rosterLoading &&
      !rosterStale &&
      // No group on screen can skip that day: none teaches it, or it has
      // passed for every one. By each group's own today (`skipGroups`), not
      // the primary's -- a day can be past there and still to come in a
      // group an organization's timezone puts behind it.
      skipGroups(date).length > 0,
    );

  const openSkipModal = (date?: string | null) => {
    if (!canSkipDate(date) || !date) return;
    setSkipTarget({
      date,
      // A skip on a later day still reads differently -- it is "scheduled", and
      // the day carries a tag until it arrives -- but the rows on and after it
      // move straight away, so the dialog says that rather than promising a
      // shift that happens later.
      // Future for every group the skip reaches, each by its own today.
      isFuture: skipGroups(date).every(
        (group) => date > (groupToday(group) ?? date),
      ),
    });
  };

  /** The error for a write that stopped part-way: what went through first,
   *  then the server's reason, which would otherwise replace it. */
  const partialError = (
    error: unknown,
    done: number,
    total: number,
    what: string,
    fallback: string,
  ) => {
    if (!done) return getError(error, fallback);
    const reason = getError(error, "");
    return `${what} for ${done} of ${total} groups; the rest failed${reason ? `: ${reason}` : "."}`;
  };

  /** Lift one group's skip for a date. A skip already gone -- lifted by an
   *  earlier, partly failed attempt -- is what was asked for, so its 404 is
   *  not an error and a retry carries on to the groups after it. */
  const deleteSkip = async (
    classroomId: number,
    date: string,
    studentIds: number[],
    groupOnly = false,
  ) => {
    try {
      await api.delete(
        `/classrooms/${classroomId}/lesson-sequencing/skip-day`,
        {
          params: {
            skipped_on: date,
            ...(studentIds.length ? { student_ids: studentIds } : {}),
            // A group skip's undo: its own row, not a student skip on the day.
            ...(groupOnly ? { group_only: true } : {}),
          },
          // FastAPI reads a repeated `student_ids=`, not the `student_ids[]=`
          // axios writes by default.
          paramsSerializer: { indexes: null },
        },
      );
    } catch (deleteError) {
      const response =
        typeof deleteError === "object" &&
        deleteError !== null &&
        "response" in deleteError
          ? (
              deleteError as {
                response?: { status?: number; data?: { detail?: unknown } };
              }
            ).response
          : undefined;
      // Only `remove_skip_day`'s own 404. "Classroom not found" is a 404 too,
      // and that removal never happened.
      if (
        response?.status !== 404 ||
        response.data?.detail !== "No skip is set for that day"
      )
        throw deleteError;
    }
  };

  /** Lift a skip that was just set, from the toast that announced it.
   *
   *  `scopedTo` is passed through so undoing a "selected students only" skip
   *  lifts only those students' rows. Without it the removal would also clear a
   *  group skip the teacher set earlier on the same day and never asked about.
   */
  const undoSkipDay = async (
    writes: {
      classroomId: number;
      studentIds: number[];
      groupOnly?: boolean;
    }[],
    date: string,
  ) => {
    let undone = 0;
    try {
      setSaving(true);
      for (const { classroomId, studentIds: scopedTo, groupOnly } of writes) {
        await deleteSkip(classroomId, date, scopedTo, groupOnly);
        undone += 1;
      }
      showNotification(
        `Skip removed for ${formatDayLabel(date)}. Original lesson dates restored.`,
        NotificationType.SUCCESS,
      );
      await refresh();
    } catch (undoError) {
      // The groups before the failure are already restored; show them so,
      // through `refresh` so a reload that also fails outranks this.
      const message = partialError(
        undoError,
        undone,
        writes.length,
        "Undid the skip",
        "Failed to undo that skip.",
      );
      if (undone) await refresh(message);
      else setError(message);
    } finally {
      setSaving(false);
    }
  };

  /** The groups `skipDay(scope, true)` leaves out as already skipping.
   *
   *  A group skip goes only to groups with no skip on the day at all: one
   *  recorded over a student skip would move those students a second day. A
   *  selected skip goes everywhere but where the whole group is off, which
   *  already covers its students -- a classmate already skipped is a no-op in
   *  `skip_day`, so a partly skipped group still gets the rest. */
  const alreadySkipping = (scope: "group" | "selected", date: string) =>
    new Set(
      groupsSkipping(date)
        .filter(
          (group) =>
            scope === "group" ||
            Boolean(
              (group.group_skipped_dates ?? group.skipped_dates)?.includes(
                date,
              ),
            ),
        )
        .map((group) => group.classroom_id),
    );

  /** `others`: only the daily groups on screen not already skipping the day
   *  (`alreadySkipping`) -- the dialog's "Skip the other N groups", and the
   *  retry after a skip that stopped part-way. */
  const skipDay = async (scope: "group" | "selected", others = false) => {
    if (!skipTarget) return;
    const skipping = alreadySkipping(scope, skipTarget.date);
    const writes = skipWrites(scope, skipTarget.date).filter(
      (write) => !others || !skipping.has(write.classroomId),
    );
    if (!writes.length) return;
    // Read before the dialog closes: `skipTarget` is null by the time the
    // teacher can reach Undo.
    const { date, isFuture } = skipTarget;
    // What was actually written, so Undo lifts exactly that even when a later
    // group's request fails part-way through.
    const done: typeof writes = [];
    // What each call recorded, which is all Undo may lift. A student or group
    // already skipping is a no-op in `skip_day`; lifting it would take back a
    // skip the teacher set earlier and never asked about.
    const recorded: {
      classroomId: number;
      studentIds: number[];
      groupOnly?: boolean;
    }[] = [];
    const undo = () =>
      recorded.length
        ? { label: "Undo", onClick: () => void undoSkipDay(recorded, date) }
        : undefined;
    try {
      setSaving(true);
      for (const write of writes) {
        const response = await api.post(
          `/classrooms/${write.classroomId}/lesson-sequencing/skip-day`,
          {
            skipped_on: date,
            scope: write.scope,
            student_ids: write.studentIds,
          },
        );
        done.push(write);
        const made = response.data ?? {};
        // An API from before `recorded_*` cannot say what it created, nor
        // lift a group skip alone: no Undo rather than a guess that could
        // take back someone else's skip.
        if (made.recorded_group) {
          recorded.push({
            classroomId: write.classroomId,
            studentIds: [],
            groupOnly: true,
          });
        } else if (made.recorded_student_ids?.length) {
          recorded.push({
            classroomId: write.classroomId,
            studentIds: made.recorded_student_ids,
          });
        }
      }
      showNotification(
        isFuture
          ? `Skip scheduled for ${formatDayLabel(date)}. Lessons on or after that day moved one school day later.`
          : `${formatDayLabel(date)} skipped. Lessons pushed forward one school day.`,
        NotificationType.SUCCESS,
        UNDO_TOAST_MS,
        undefined,
        undo(),
      );
      setSkipTarget(null);
      await refresh();
    } catch (skipError) {
      // The groups before the failure were skipped: they keep their Undo, the
      // roster shows them as they are, and the dialog stays open on the
      // groups still to skip ("Skip the other N groups").
      const message = partialError(
        skipError,
        done.length,
        writes.length,
        `Skipped ${formatDayLabel(date)}`,
        "Failed to skip that day.",
      );
      if (done.length) {
        showNotification(
          `${formatDayLabel(date)} skipped for ${done.length} of ${writes.length} groups.`,
          NotificationType.SUCCESS,
          UNDO_TOAST_MS,
          undefined,
          undo(),
        );
        // Through `refresh`, so a reload that also fails outranks this.
        await refresh(message);
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const removeSkipDay = async () => {
    if (!skipTarget) return;
    // Every group on screen that skips the day -- the ones the dialog named.
    const groups = groupsSkipping(skipTarget.date);
    if (!groups.length) return;
    const { date } = skipTarget;
    let removed = 0;
    try {
      setSaving(true);
      for (const group of groups) {
        await deleteSkip(group.classroom_id, date, []);
        removed += 1;
      }
      showNotification(
        `Scheduled skip removed for ${formatDayLabel(date)}. Original lesson dates restored.`,
        NotificationType.SUCCESS,
      );
      setSkipTarget(null);
      await refresh();
    } catch (removeError) {
      // Each removal commits on its own. Refreshed, the dialog names only the
      // groups still skipping, so a retry picks up where this stopped.
      const message = partialError(
        removeError,
        removed,
        groups.length,
        "Removed the skip",
        "Failed to remove that skip.",
      );
      if (removed) await refresh(message);
      else setError(message);
    } finally {
      setSaving(false);
    }
  };

  /** Move a set of students back the way they came.
   *
   *  Only the students the shift actually moved, so a second press of Undo
   *  cannot walk students who never moved. The reverse call is the same
   *  endpoint with the delta negated, which is exact in both directions only
   *  because `shift_days` passes `refuse_on_floor=True`: a student who cannot
   *  move the whole way does not move at all, so every row the shift touched
   *  moved by the same amount and negating it puts them all back.
   *
   *  Exact, but not guaranteed: the inverse can still be refused if something
   *  re-occupies today between the move and the undo, so the result is read
   *  back rather than assumed.
   */
  const undoShift = async (
    classroomId: number,
    movedIds: number[],
    delta: number,
  ) => {
    if (!movedIds.length) return;
    try {
      setSaving(true);
      const response = await api.post(
        `/classrooms/${classroomId}/lesson-sequencing/shift-days`,
        { student_ids: movedIds, delta: -delta },
      );
      // Read the response rather than assuming the inverse succeeded. This
      // reported `movedIds.length` unconditionally, so an undo the server had
      // refused still told the teacher it had worked -- "Move undone for 2
      // students" over a call that moved zero rows. The refusal is real:
      // `shift_days` is all-or-nothing per student, and a student whose
      // schedule cannot move the whole way does not move at all.
      const skippedIds: number[] = response.data?.skipped_student_ids ?? [];
      const undone = movedIds.length - skippedIds.length;
      if (undone > 0) {
        showNotification(
          `Move undone for ${undone} ${undone === 1 ? "student" : "students"}.`,
          NotificationType.SUCCESS,
        );
      }
      await refresh(
        skippedIds.length
          ? `Undid the move for ${undone} of ${movedIds.length} ${movedIds.length === 1 ? "student" : "students"}. ${skippedIds.length === 1 ? "1 schedule" : `${skippedIds.length} schedules`} could not move back, because a lesson would land before today.`
          : undefined,
      );
    } catch (undoError) {
      setError(getError(undoError, "Failed to undo that move."));
    } finally {
      setSaving(false);
    }
  };

  const shiftSchedules = async (direction: "back" | "forward") => {
    // The selection's group, as the buttons, the dialog's count and Change
    // lesson all read it -- not `primary`, the page's settings group. With
    // several groups on screen those differ, and filtering by `primary` left
    // nobody to send: GDS Group B keeps each student in their own group, so
    // Confirm on Dillian made no request at all (2026-09-29).
    const classroomId = changeTargetClassroomId;
    const studentIds = selectionStudentIds;
    if (classroomId == null || !studentIds.length) return;
    const delta = direction === "forward" ? 1 : -1;
    try {
      setSaving(true);
      const response = await api.post(
        `/classrooms/${classroomId}/lesson-sequencing/shift-days`,
        { student_ids: studentIds, delta },
      );
      setShiftConfirm(null);
      // The response says who could not be moved. A flat success over a partial
      // result is how a teacher ends up believing a roster shifted when it did
      // not.
      const skippedIds: number[] = response.data?.skipped_student_ids ?? [];
      // Who actually moved -- the only students an undo may touch. Falls back
      // to everyone asked for so an older response without the field still
      // describes the move it made.
      const movedIds: number[] =
        response.data?.shifted_student_ids ??
        studentIds.filter((id) => !skippedIds.includes(id));
      const moved = studentIds.length - skippedIds.length;
      // Reported even on a partial move, rather than instead of the toast: the
      // students who did move are the destructive half, and they were the ones
      // left without an undo when this only spoke up on a clean run. Carried
      // through `refresh` -- see there for why setting it here did nothing.
      // In No expectation mode a student is skipped at an edge -- back from
      // the first lesson, forward from the last -- or while still waiting for
      // a starting lesson (decision 14); the rest still move.
      const partial = skippedIds.length
        ? pointerMode
          ? `Moved ${moved} of ${studentIds.length} students. ${skippedIds.length} could not move ${direction}: they are at the ${direction === "back" ? "first" : "last"} lesson, or still need a starting lesson.`
          : `Moved ${moved} of ${studentIds.length} students. ${skippedIds.length} could not be moved ${direction}${direction === "back" ? ", because a lesson would land before today" : " without putting two lessons on one day"}.`
        : undefined;
      // No expectation mode answers `undoable: false`: the opposite step there
      // is another teacher move, and a step back starts a new round rather
      // than restoring the old one.
      const undoable = response.data?.undoable !== false;
      if (moved > 0) {
        showNotification(
          pointerMode
            ? `Moved ${moved} ${moved === 1 ? "student" : "students"} 1 lesson ${direction}.${direction === "back" ? " Each works through the lessons from there again." : ""}`
            : `Moved ${moved} ${moved === 1 ? "student's schedule" : "students' schedules"} 1 day ${direction}.`,
          NotificationType.SUCCESS,
          UNDO_TOAST_MS,
          undefined,
          undoable
            ? {
                label: "Undo",
                onClick: () => void undoShift(classroomId, movedIds, delta),
              }
            : undefined,
        );
      }
      setSelectedStudents(new Set());
      await refresh(partial);
    } catch (shiftError) {
      setError(getError(shiftError, "Failed to move those schedules."));
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    if (!canEditSequencing) return;
    if (!primary) return;
    try {
      setSaving(true);
      setDialogError(null);
      const body = {
        // No `sequence_id`. This dialog has no control that picks one, so it
        // was only ever echoing back the group's own value -- harmless while
        // the save went to that group alone, and a 409 the moment the same
        // body reaches a group bound to a different sequence with students
        // placed on it. The writer only assigns it `if patch.sequence_id is
        // not None`, so leaving it out is the no-op that echoing it used to be.
        // Weekly mode carries its total here, at one lesson a day. Daily
        // mode still runs the full five days and varies the cap instead.
        // Written explicitly in both branches so a group carrying an older
        // value is brought in line the next time its settings are saved.
        // The weekly batch has its own column since 0072; the days field
        // means only which weekdays serve, so it stays at the full week.
        expected_days_per_week: MAX_DAYS_PER_WEEK,
        expected_lessons_per_day: weeklyMode ? 1 : daily,
        expected_lessons_per_week: weeklyTotal,
        incomplete_mode: mode,
        assignment_mode: assignmentMode,
      };
      // Into or out of No expectation mode is not a settings save (R2, R37):
      // the mode moves with every student's starting point, confirmed in the
      // review, in one request -- and the PATCH refuses it. The pace this
      // dialog holds is kept for the way out, saved while the teacher is
      // still in the mode so the new pace plans from the picks.
      const storedMode = overview?.teacher_settings.assignment_mode;
      if (isModeSwitch(storedMode, assignmentMode)) {
        setSettingsOpen(false);
        openModeSwitchReview(
          assignmentMode,
          isPointerMode(assignmentMode)
            ? null
            : { ...body, assignment_mode: storedMode },
        );
        return;
      }
      // Teacher-wide, always. Lesson settings are one pace for the teacher's
      // groups; there is no per-group pacing in the product, so there is no
      // scope for this to compute.
      //
      // `apply_to_all` is what makes that true of the groups as well as the
      // default: it clears the pace fields this body carries off every
      // classroom, so a group still holding its own from when the dialog wrote
      // one at a time stops masking what was just set. Without it those groups
      // keep serving a value no control can reach any more.
      //
      // One request, one transaction. The backend walks the classrooms.
      await api.patch("/lesson-sequencing/settings", {
        // No editable pace in this mode. Keep the stored paced preferences,
        // including classroom overrides, until a reviewed switch out.
        ...(isPointerMode(assignmentMode)
          ? { assignment_mode: assignmentMode }
          : body),
        apply_to_all: true,
      });
      setSettingsOpen(false);
      const pace = isPointerMode(assignmentMode)
        ? "No expectation"
        : weeklyMode
          ? `${weeklyTotal} expected/week`
          : `${daily} expected/day (${weeklyTotal}/week)`;
      showNotification(
        `Lesson rules saved for ${settingsScopeLabel}: ${pace}`,
        NotificationType.SUCCESS,
      );
      await refresh();
    } catch (saveError) {
      reportDialogError(
        "settings",
        getError(saveError, "Failed to save lesson settings."),
      );
    } finally {
      setSaving(false);
    }
  };

  const setAutoSequencing = async (nextEnabled: boolean) => {
    if (!canEditSequencing) return;
    try {
      setSaving(true);
      // `apply_to_all` clears every stored per-group exception as it writes the
      // default. The spec has one teacher-wide toggle and no per-group
      // sequencing state, so an override left in the database would be a reason
      // for this switch not to take -- and no longer one the teacher can see or
      // undo, now that the control that set it is gone.
      await api.patch("/lesson-sequencing/settings", {
        default_enabled: nextEnabled,
        apply_to_all: true,
      });
      showNotification(
        `Auto-sequencing turned ${nextEnabled ? "on" : "off"} for all your groups`,
        NotificationType.SUCCESS,
      );
      await refresh();
    } catch (saveError) {
      setError(getError(saveError, "Failed to update auto-sequencing."));
    } finally {
      setSaving(false);
    }
  };

  const toggleAutoSequencing = async () => {
    if (!teacherEnabled) {
      // The review *is* the way in, unconditionally -- `toggleAutoSequencing`
      // in the export is `setActivationReviewOpen(true)` with no roster test.
      // A plain "Turn on Auto-sequencing?" confirmation used to stand in when
      // the roster was empty, which meant the one screen that explains what
      // turning it on does was the screen a teacher with no placed students
      // never saw. "Approve and turn on" is the only way it enables now.
      setModeSwitch(null);
      setSelections({});
      setReviewEdited(new Set());
      setReviewCommitted({});
      setReviewedAll(false);
      setReviewOnlyUnplaced(false);
      reviewRequestRef.current += 1;
      setReviewDetails(null);
      setReviewOpen(true);
      void loadOverview().then((loaded) => {
        if (!loaded) void loadReviewDetails();
      });
      return;
    }
    await setAutoSequencing(false);
  };

  // Kept as the list, not a boolean: a roster carries no suggestions at all
  // until the first activation, so "some student, somewhere, is unset" is the
  // normal starting state and the teacher needs to know *which* ones.
  // "Placed" means the classroom's sequence can actually serve the selection,
  // not merely that some id is set. Activation rejects a node absent from the
  // current sequence -- `invalid_nodes` in `activate` returns 422 "Starting
  // lesson is not in the selected sequence" -- and a row seeded from
  // `suggested_curriculum_node_id` can name exactly that, once a version is
  // retired or the classroom is re-bound. Counting it as placed enabled
  // "Approve and turn on" against a payload the API would refuse, with nothing
  // saying which rows to fix.
  //
  // Deliberately strict when a classroom has no options: `reviewRoster` and
  // `reviewOptionsByClassroom` derive from the same fetch, so an empty list
  // means the sequence really offers nothing and no selection can be valid.
  const selectionIsInSequence = (
    student: StudentRow & { classroom_id: number },
  ) => {
    const chosen = selections[rowKey(student)];
    if (!chosen) return false;
    return (reviewOptionsByClassroom.get(student.classroom_id) ?? []).some(
      (option) => option.curriculum_node_id === chosen,
    );
  };
  const unplacedStudents = reviewRoster.filter(
    (student) => !selectionIsInSequence(student),
  );
  // `reviewDetails` is null until the roster resolves, so an empty roster is
  // distinguishable from an unloaded one: the first must not arm the button,
  // the second is vacuously placed. A teacher whose groups have no students yet
  // still has a teacher-wide switch to turn on.
  const everyStudentPlaced =
    reviewDetails !== null && unplacedStudents.length === 0;
  // Resuming preserves exhausted and unplaced students too. Only explicit
  // changes must name a lesson the sequence can currently serve.
  const activationReady =
    reviewDetails !== null &&
    reviewSeeded &&
    (pointerReview
      ? reviewRoster.every(
          (student) =>
            !reviewEdited.has(rowKey(student)) ||
            selectionIsInSequence(student),
        )
      : everyStudentPlaced);
  // Derived, so placing the last student releases the filter instead of leaving
  // an empty table whose only escape hatch lives in the message that just
  // disappeared.
  const onlyUnplaced = reviewOnlyUnplaced && unplacedStudents.length > 0;

  const reviewGroups = Array.from(
    new Set(reviewRoster.map((student) => student.classroom_name)),
  );
  const reviewStudents = reviewRoster.filter((student) => {
    const term = reviewSearch.trim().toLowerCase();
    if (term && !student.name.toLowerCase().includes(term)) return false;
    if (onlyUnplaced && selections[rowKey(student)]) return false;
    return (
      reviewGroup === "All groups" || student.classroom_name === reviewGroup
    );
  });
  const reviewVisibleSelected =
    reviewStudents.length > 0 &&
    reviewStudents.every((student) => reviewSelected.has(rowKey(student)));

  // Bulk apply acts on what the teacher can actually see and has selected.
  // Ids linger in `reviewSelected` after their rows leave the table — a row
  // drops out the moment it is placed while "Show only these" is on, and search
  // hides rows the same way — and applying to those too would silently
  // overwrite a placement made seconds earlier with the next lesson chosen.
  // Bulk apply spans groups, and lesson options are per group, so offer only
  // what every group currently in scope shares. Offering one group's options
  // to all of them would write a node the others cannot resolve.
  const bulkOptions = (() => {
    const groups = Array.from(
      new Set(reviewStudents.map((student) => student.classroom_id)),
    ).map((id) => reviewOptionsByClassroom.get(id) ?? []);
    if (!groups.length) return [] as LessonOption[];
    const [first, ...rest] = groups;
    return first.filter((option) =>
      rest.every((other) =>
        other.some(
          (candidate) =>
            candidate.curriculum_node_id === option.curriculum_node_id,
        ),
      ),
    );
  })();

  const bulkTargetKeys = reviewStudents
    .filter((student) => reviewSelected.has(rowKey(student)))
    .map((student) => rowKey(student));

  const applyBulkLesson = () => {
    if (saving || !bulkNode || bulkTargetKeys.length === 0) return;
    setSelections((current) => {
      const next = { ...current };
      bulkTargetKeys.forEach((key) => {
        next[key] = bulkNode;
      });
      return next;
    });
    setReviewEdited((current) => {
      const next = new Set(current);
      bulkTargetKeys.forEach((key) => next.add(key));
      return next;
    });
    // Only while the unplaced filter is on: the rows just applied to leave the
    // table, and leaving them selected would put them back in range of a later
    // Apply the moment the teacher switches back to the full roster. Unfiltered
    // they stay put and stay selected, so applying a different lesson to the
    // same set does not mean re-picking it.
    if (onlyUnplaced) {
      setReviewSelected((current) => {
        const next = new Set(current);
        bulkTargetKeys.forEach((key) => next.delete(key));
        return next;
      });
    }
  };

  /** Open the review as the starting-point step of a mode switch (R2, R37).
   *
   *  Every row starts over from the switch's own seed -- the student's Current
   *  when leaving, the server's suggestion when entering -- rather than from
   *  choices left by an earlier activation. */
  const openModeSwitchReview = (
    target: AssignmentMode,
    pace: Record<string, unknown> | null,
  ) => {
    setModeSwitch({ target, pace });
    setSelections({});
    setReviewEdited(new Set());
    setReviewCommitted({});
    setReviewedAll(false);
    setReviewOnlyUnplaced(false);
    // Read fresh, and nothing confirmable until it arrives: the roster the
    // page loaded earlier may predate a student enrolled since, and a switch
    // confirmed against it would leave that student waiting for placement.
    // The group list too: a group created since would be missing from the
    // review, and the switch refuses one that does not name every group. A
    // new overview re-reads the roster through the effect on
    // `loadReviewDetails`; failing that, the roster is read as it stands.
    // A read already in flight -- the page's own, from before the switch --
    // must not land here: its answer may predate the same changes.
    reviewRequestRef.current += 1;
    setReviewDetails(null);
    setReviewOpen(true);
    void loadOverview().then((loaded) => {
      if (!loaded) void loadReviewDetails();
    });
  };

  /** Switch modes: the pace first when leaving, then every group at once. */
  const confirmModeSwitch = async () => {
    // An unloaded roster is no picks at all, and the switch would leave every
    // student waiting for placement. Only a roster that arrived is reviewed.
    if (
      !canEditSequencing ||
      !modeSwitch ||
      reviewDetails === null ||
      !reviewSeeded
    )
      return;
    const entering = isPointerMode(modeSwitch.target);
    // Only picks the group's sequence can serve; the rest wait for placement
    // rather than fail the whole switch on a node it would refuse.
    const placed: Record<string, number> = {};
    reviewRoster.forEach((student) => {
      if (selectionIsInSequence(student)) {
        placed[rowKey(student)] = selections[rowKey(student)];
      }
    });
    try {
      setSaving(true);
      setDialogError(null);
      // The pace leaving the mode rides in the switch, not in a settings save
      // before it: one transaction, so a refused switch changes nothing (R32).
      const response = await api.post<{
        classrooms: { pending_student_ids: number[] }[];
      }>(
        "/lesson-sequencing/mode-switch",
        modeSwitchBody(
          modeSwitch.target,
          reviewRoster,
          placed,
          modeSwitch.pace as Parameters<typeof modeSwitchBody>[3],
          // Every reviewed group, students or not: the switch refuses one
          // left out, and an empty group has no rows to name it.
          reviewDetails.map((detail) => detail.classroom_id),
        ),
      );
      const waiting = (response.data?.classrooms ?? []).reduce(
        (count, classroom) => count + classroom.pending_student_ids.length,
        0,
      );
      setReviewOpen(false);
      showNotification(
        !teacherEnabled
          ? entering
            ? "No expectation mode is set. Students start at the lessons you confirmed once Auto-sequencing is on."
            : "Lesson pacing is set. Students start from the lessons you confirmed once Auto-sequencing is on."
          : entering
            ? "No expectation mode is on. Each student starts at the lesson you confirmed."
            : "Lesson pacing is on. Each student's schedule starts from the lesson you confirmed.",
        NotificationType.SUCCESS,
      );
      await refresh(
        waiting
          ? `${waiting} ${waiting === 1 ? "student still needs" : "students still need"} a starting lesson. Use Change lesson to place them.`
          : undefined,
      );
    } catch (switchError) {
      // Kept open with the picks as they were, so the teacher can retry (R32).
      reportDialogError(
        "review",
        getError(
          switchError,
          "Failed to switch modes. Students' lessons were not changed.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const activate = async () => {
    if (!canEditSequencing) return;
    if (!reviewedAll || !activationReady) return;
    // Activation is per classroom -- that is where a sequence and a roster
    // live -- but the switch it releases is teacher-wide, so this posts one
    // activation per group and only then writes the default. Ordered that way
    // deliberately: turning the default on first would enable groups whose
    // students have not been placed yet if a later activation failed.
    const byClassroom = new Map<
      number,
      { student_id: number; curriculum_node_id: number; re_picked: boolean }[]
    >();
    // Row keys per classroom, so a committed classroom's marks can be dropped
    // the moment its POST returns.
    const keysByClassroom = new Map<number, string[]>();
    reviewRoster.forEach((student) => {
      const node = selections[rowKey(student)];
      if (!node) return;
      const group = byClassroom.get(student.classroom_id) ?? [];
      group.push({
        student_id: student.student_id,
        curriculum_node_id: node,
        re_picked: reviewEdited.has(rowKey(student)),
      });
      byClassroom.set(student.classroom_id, group);
      keysByClassroom.set(student.classroom_id, [
        ...(keysByClassroom.get(student.classroom_id) ?? []),
        rowKey(student),
      ]);
    });
    try {
      setSaving(true);
      setDialogError(null);
      // Sequentially, not Promise.all: each activation writes sequences and
      // schedule rows, and a half-applied fan-out is harder to reason about
      // than a stop at the first failure with the rest untouched.
      // Nothing to place when the roster is empty, and that is not a reason to
      // withhold the switch -- the loop simply does not run.
      if (pointerReview) {
        // Pointer activation has no dated schedule to rebuild. Preserve every
        // untouched placement and apply edits through the existing move API.
        for (const student of reviewRoster) {
          const key = rowKey(student);
          if (!reviewEdited.has(key)) continue;
          const response = await api.post<{ pending_student_ids: number[] }>(
            `/classrooms/${student.classroom_id}/lesson-sequencing/change-lesson`,
            {
              student_ids: [student.student_id],
              curriculum_node_id: selections[key],
            },
          );
          if (response.data.pending_student_ids.length) {
            reportDialogError(
              "review",
              `Could not place ${student.name}. Review their starting lesson and try again.`,
            );
            return;
          }
          // A later failure must not replay a committed move on retry.
          setReviewCommitted((current) => ({
            ...current,
            [key]: selections[key],
          }));
          setReviewEdited((current) => {
            const next = new Set(current);
            next.delete(key);
            return next;
          });
        }
      } else {
        for (const [classroomId, students] of byClassroom) {
          await api.post(
            `/classrooms/${classroomId}/lesson-sequencing/activation`,
            { reviewed_all: true, students },
          );
          // Committed, so no longer a re-pick. A later classroom can still fail,
          // and the modal stays open on that -- retrying would otherwise re-send
          // this one's rows as deliberate choices and re-run placement against
          // students already placed, undoing whatever they did in between.
          const committed = keysByClassroom.get(classroomId) ?? [];
          setReviewEdited((current) => {
            const next = new Set(current);
            committed.forEach((key) => next.delete(key));
            return next;
          });
        }
      }
      await api.patch("/lesson-sequencing/settings", {
        default_enabled: true,
        apply_to_all: true,
      });
      setReviewOpen(false);
      showNotification(
        pointerReview
          ? "Auto-sequencing turned on. Reviewed lesson placements preserved."
          : "Auto-lesson sequencing activated. Starting points approved for all students.",
        NotificationType.SUCCESS,
      );
      await refresh();
    } catch (activationError) {
      reportDialogError(
        "review",
        getError(activationError, "Failed to activate lesson sequencing."),
      );
    } finally {
      setSaving(false);
    }
  };

  // Writes are refused while the rows on screen are not known to be current:
  // mid-page, or left behind by a failed one. The dimming says so, but dimming
  // is not a guard -- the cells stay clickable underneath it.
  const rosterActionable = !rosterLoading && !rosterStale;

  const openChangeModal = () => {
    if (!rosterActionable) return;
    // Preselect only when every selected student is on the same lesson.
    // Otherwise the picker opens empty: the previous fallback was
    // `changeLessonOptions[0]`, the first lesson in the *curriculum*, so a
    // teacher who opened the modal on a mixed selection and clicked Save sent
    // the whole group back to lesson one.
    setTargetNode(sharedCurrentLesson(rosterStudents, selectedStudents));
    setChangeConfirming(false);
    setChangeOpen(true);
  };

  /** Change-lesson for one student, from clicking a cell or a lesson in it. */
  const openChangeForStudent = (studentId: number) => {
    if (!rosterActionable) return;
    setSelectedStudents(new Set([studentId]));
    // One student, so their own current lesson is the preselection -- computed
    // from the roster directly rather than through the selection-derived
    // memo, which has not recomputed yet and would still describe whoever was
    // selected before.
    setTargetNode(sharedCurrentLesson(rosterStudents, new Set([studentId])));
    setChangeConfirming(false);
    setChangeOpen(true);
  };

  const closeChangeModal = () => {
    setChangeOpen(false);
    setChangeConfirming(false);
  };

  const changeLesson = async () => {
    if (!canEditSequencing) return;
    if (!primary) return;
    // `selectedOption` is the guard, `targetNode` is the pick.
    //
    // The guard, because a `targetNode` that resolves to no entry is not a
    // selection the teacher ever saw -- the picker showed its placeholder --
    // and the server rejects it. The pick, because the two differ for an
    // untouched preselection: the student sits on the later copy of a
    // duplicated lesson and the picker offers the earlier one. Sending the
    // option's node there would relocate the student backward over every
    // lesson between the two slots, while `changeLessonImpact` -- which
    // compares against `targetNode` -- labelled the confirmation "No change".
    if (!selectedOption || targetNode === null || !changeTargetClassroomId)
      return;
    const studentIds = selectionStudentIds;
    if (!studentIds.length) return;
    try {
      setSaving(true);
      setDialogError(null);
      const response = await api.post(
        `/classrooms/${changeTargetClassroomId}/lesson-sequencing/change-lesson`,
        {
          student_ids: studentIds,
          curriculum_node_id: targetNode,
          disposition,
          // The day the teacher is looking at. `ChangeLessonRequest.day_offset`
          // exists for this: "the change lands on the day the teacher is
          // looking at rather than on today". A lesson already scheduled
          // further out is moved onto that day, which is what bringing one
          // forward means from the roster.
          //
          // The response's offset, not the local one. The rows on screen came
          // from this response, so this is the day the teacher is actually
          // looking at; `dayOffset` is where they asked to go, which is a
          // different day whenever a page is in flight or its request failed.
          // Sending that moved a lesson on a day the roster was not showing.
          day_offset: primary.day_offset,
        },
      );
      closeChangeModal();
      setTargetNode(null);
      setSelectedStudents(new Set());
      // The response says who it could not move. Announcing a flat success
      // while some students were skipped is how a teacher ends up believing a
      // roster is placed when it is not.
      const skipped: number[] = response.data?.pending_student_ids ?? [];
      const moved = studentIds.length - skipped.length;
      // Carried through `refresh` -- see there for why setting it here did
      // nothing.
      let partial: string | undefined;
      if (pointerMode) {
        // A pick of the lesson a student is already on moves nothing: they
        // are neither changed nor waiting, and the response says so.
        const changed: number[] = response.data?.changed_student_ids ?? [];
        const unchanged: number[] = response.data?.unchanged_student_ids ?? [];
        if (skipped.length) {
          partial = `Changed ${changed.length} of ${studentIds.length} students. ${skipped.length} could not be placed on that lesson. Reload and try again.`;
        }
        if (changed.length) {
          showNotification(
            `Changed current lesson for ${changed.length} ${changed.length === 1 ? "student" : "students"}.${unchanged.length ? ` ${unchanged.length} already ${unchanged.length === 1 ? "was" : "were"} on that lesson.` : ""}`,
            NotificationType.SUCCESS,
          );
        } else if (unchanged.length) {
          showNotification(
            `${unchanged.length === 1 ? "That student is" : "Those students are"} already on that lesson. Nothing changed.`,
            NotificationType.SUCCESS,
          );
        }
      } else if (skipped.length) {
        partial = `Changed ${moved} of ${studentIds.length} students. ${skipped.length} could not be moved. Reload and try again.`;
      } else {
        // Moving back, a current lesson kept finishable follows the new one
        // rather than going to Incomplete, and the response says for whom: in
        // a bulk change some students can move back and others forward.
        const followed: number[] =
          response.data?.followed_pick_student_ids ?? [];
        // An already-completed current lesson keeps its day instead
        // (product, 2026-10-04).
        const kept: number[] = response.data?.kept_completed_student_ids ?? [];
        const setAside = studentIds.length - followed.length - kept.length;
        const pile =
          disposition === "push_incomplete" ? "Incomplete" : "Skipped";
        const all = (count: number) => count === studentIds.length;
        const outcome = [
          followed.length > 0 &&
            (all(followed.length)
              ? `Previous ${followed.length === 1 ? "lesson follows" : "lessons follow"} the new one.`
              : `For ${followed.length} the previous lesson follows the new one.`),
          kept.length > 0 &&
            (all(kept.length)
              ? `Previous ${kept.length === 1 ? "lesson was" : "lessons were"} already completed and ${kept.length === 1 ? "stays on its" : "stay on their"} day.`
              : `For ${kept.length} the previous lesson was already completed and stays on its day.`),
          setAside > 0 &&
            (all(setAside)
              ? `Previous ${setAside === 1 ? "lesson" : "lessons"} moved to ${pile}.`
              : `For ${setAside} it moved to ${pile}.`),
        ]
          .filter(Boolean)
          .join(" ");
        showNotification(
          `Changed current lesson for ${studentIds.length} ${studentIds.length === 1 ? "student" : "students"}. ${outcome}`,
          NotificationType.SUCCESS,
        );
      }
      await refresh(partial);
    } catch (changeError) {
      // The pick and the selection stay as they were, so Try again resends
      // exactly what failed (R32).
      reportDialogError(
        "change",
        getError(changeError, "Failed to change lesson."),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LessonSequencingSkeleton />;
  if (error && !overview) {
    return (
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <p className="text-red-600">{error}</p>
        <PrimaryButton
          onClick={() => {
            setLoading(true);
            void loadOverview();
          }}
        >
          Try again
        </PrimaryButton>
      </div>
    );
  }
  if (!overview?.classrooms.length) {
    return (
      <div className="rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] p-12 text-center font-semibold text-[#687368]">
        Create a group before opening lesson sequencing.
      </div>
    );
  }
  // A failed roster fetch is not a loading state.
  //
  // `loadDetails` reports failure through `setError` and leaves `details` null,
  // so `primary` stays null -- but the only error branch above is gated on
  // `error && !overview`, and the overview loads through its own request. A
  // details failure therefore skipped it, fell through here, and sat under the
  // loading state forever with the error never rendered.
  //
  // That is what a permanently pulsing page was: not a reload loop, a stuck
  // load whose cause was on screen nowhere. Same retry as the overview branch,
  // because retrying is the only useful thing to offer.
  if (!primary && error) {
    return (
      <div className="flex flex-col items-center gap-4 p-12 text-center">
        <p className="text-red-600">{error}</p>
        <PrimaryButton
          onClick={() => {
            setError(null);
            void loadDetails(classroomIds, dayOffset);
          }}
        >
          Try again
        </PrimaryButton>
      </div>
    );
  }

  // The same skeleton as the `loading` branch above, not a bare spinner.
  //
  // The page loads in two sequential requests: `loadOverview` clears `loading`,
  // then `loadDetails` fills `details`, and `primary` is only found once the
  // second lands. A different loading UI for the second phase meant a full page
  // of skeleton appeared, vanished to a centred spinner, and reappeared as
  // content -- three distinct screens for one navigation, which reads as the
  // page reloading itself rather than loading once.
  //
  // Kept after the error and no-classrooms guards on purpose: reached earlier,
  // a failed fetch would sit under an endless skeleton instead of showing what
  // went wrong.
  if (!primary) return <LessonSequencingSkeleton />;

  // On or Off, nothing else. The spec is explicit that there is "no mandatory
  // review dialog and no Pending Review state" -- quoted in
  // `sequencing/service.py`, in `update_teacher_settings` -- and the card was
  // still showing one. Nothing is lost by dropping it: `toggleAutoSequencing`
  // opens the review unconditionally while sequencing is off, so the label
  // never gated anything, and a third state on a two-state switch read as the
  // switch disagreeing with itself.
  //
  // Reads the same source as the switch beside it. It used to read the
  // classroom's effective setting while the switch read the teacher-wide one,
  // so the card could show "On" above a switch sitting in the off position --
  // two halves of one control disagreeing.
  // "Unavailable" rather than On/Off when the organization has no curriculum to
  // schedule from. The stored switch is left exactly as the teacher set it --
  // the curriculum coming back must restore sequencing without them having to
  // remember to switch it on again -- so the label cannot simply read the
  // stored value here: it would say "On" over a surface that serves nothing.
  const autoSequencingLabel = !curriculumAvailable
    ? "Unavailable"
    : teacherEnabled
      ? "On"
      : "Off";
  // Saved pace, as opposed to the in-flight modal values above.
  // The teacher's total, not the selected group's. This is the summary on a
  // card whose dialog is teacher-wide and seeds from the same place, so
  // reading it off `primary` let a group with a legacy override show one
  // number on the card and another inside the modal -- and change the
  // "global" figure as the teacher paged between groups.
  //
  // Read, not multiplied. The batch is stored directly since 0072, and
  // multiplying the days field by the daily cap would report a daily
  // classroom's week as its cap times five -- right by accident today, wrong
  // the moment either field moves independently.
  const savedWeeklyTotal =
    overview?.teacher_settings.expected_lessons_per_week ??
    primary.settings.expected_lessons_per_week;
  const settingsOwnerName =
    selectedAccount && !selectedAccount.is_you
      ? selectedAccount.name ||
        selectedAccount.username ||
        selectedAccount.email
      : null;
  // Only "No lessons": the panel that shows this is not rendered while
  // auto-sequencing is off, so there is no off-state cell left to label.
  const rosterEmptyLabel = "No lessons";
  // No expectation mode names an empty Previous for what it is (R29); Current
  // and Next read "No lessons" as before -- at the end, Next has none left.
  const previousEmptyLabel = pointerMode
    ? "No previous lesson"
    : rosterEmptyLabel;
  const autoSequencingColor =
    teacherEnabled && curriculumAvailable ? "#28543D" : "#687368";
  return (
    <div className="flex flex-col gap-12 font-mulish text-[#294B39]">
      <style>{`
        .sequencing-controls {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          align-items: end;
          gap: 28px;
          padding: 40px;
          border-bottom: 1px solid #DCE1E8;
        }
        .student-toolbar {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: center;
          gap: 24px;
        }
        .student-toolbar-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          /* Wraps at every width, not just under 700px. Without it the actions
             track refuses to shrink and takes its space out of the heading
             column beside it, which is a collision rather than a layout. */
          flex-wrap: wrap;
        }
        .student-search { position: relative; width: 240px; flex: 0 0 240px; }
        .assignment-compact-list { display: none; }
        .compact-pager { display: none; }
        .assignment-desktop-table {
          display: block;
          background: #F3EDE2;
          border-top: 1px solid #DED8CC;
        }
        /* Separate borders with row spacing are what make each row read as its
           own rounded card rather than a table rule. */
        .assignment-desktop-table table {
          width: calc(100% - 32px);
          margin: 0 16px 10px;
          border-collapse: separate;
          border-spacing: 0 8px;
          /* The one place this diverges from the export's CSS, and it is what
             makes the columns match it on screen. Auto layout treats the widths
             on the header cells as hints and hands the slack to whichever
             column holds the longest text. The export gets away with that
             because its mock has short group names ("Math 1A") and long lesson
             titles ("Identify equivalent expressions"), so content alone lands
             the day columns near 23% each. Real data inverts it — group names
             like "Grade 3 Student test 2 1" against a title like "A Space
             Odyssey" — so the student column swallowed the row and the day
             cells collapsed to half their width, wrapping two-word titles onto
             two lines. Fixed layout makes the declared widths authoritative, so
             the proportions hold whatever the content is.

             The day columns deliberately declare no width: under fixed layout
             the columns without one split whatever is left after the fixed
             ones, so they stay equal thirds at any container size. Pinning them
             to 23% each was exact only near 1335px and overflowed into a
             horizontal scrollbar between there and the 1100px breakpoint. */
          table-layout: fixed;
        }
        .assignment-desktop-table thead th { background: #F3EDE2; }
        .assignment-desktop-table tbody td {
          background: #FBF8F1;
          border-top: 1px solid #DED8CC;
          border-bottom: 1px solid #DED8CC;
        }
        .assignment-desktop-table tbody td:first-child {
          border-left: 1px solid #DED8CC;
          border-radius: 8px 0 0 8px;
        }
        .assignment-desktop-table tbody td:last-child {
          border-right: 1px solid #DED8CC;
          border-radius: 0 8px 8px 0;
        }
        .assignment-desktop-table tbody tr.is-selected td { border-color: #D6CEBF; }
        /* The design switches at 1100px, not at Tailwind's md. */
        @media (max-width: 1100px) {
          .assignment-compact-list { display: grid; gap: 12px; }
          .compact-pager { display: flex; }
          .assignment-desktop-table { display: none; }
          .sequencing-controls { padding: 24px; }
        }
        @media (max-width: 700px) {
          .student-toolbar { grid-template-columns: minmax(0, 1fr); gap: 16px; }
          .student-toolbar-actions { justify-content: flex-start; }
          .student-search { width: 100%; flex-basis: 100%; }
        }
      `}</style>
      <header className="et-welcome et-sequencing-welcome">
        <div>
          <h1>
            Lesson <em>Sequencing</em>
          </h1>
          <p>Manage each student’s learning path from one place.</p>
        </div>
        <div className="et-sequencing-switch">
          <div className="flex items-center justify-between gap-5 px-5 py-4">
            <div>
              <span className="block text-sm font-bold">Auto-sequencing</span>
              <span
                className="mt-1 block text-xs font-semibold"
                style={{ color: autoSequencingColor }}
              >
                {autoSequencingLabel}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-label={
                settingsOwnerName
                  ? `Auto-sequencing for ${settingsOwnerName}'s groups`
                  : "Auto-sequencing for all your groups"
              }
              aria-checked={teacherEnabled}
              onClick={() => void toggleAutoSequencing()}
              // Only the off-to-on move is blocked. The off switch has to
              // stay reachable while sequencing is on, because
              // `curriculum_available` is *stricter* than the rule the
              // backend gate applies and the two disagree in a state that is
              // reachable:
              //
              // `_org_has_live_sequence` requires an assignable lesson behind
              // a live node and a published version, so an organization still
              // bound to a curriculum whose items have all been retired,
              // unpublished or deleted reports `curriculum_available: false`.
              // Nothing clears `sequence_id` in that case -- the binding
              // fan-out reacts to bindings, not to lesson health -- so
              // `sequencing_serves` stays true and
              // `_assert_manual_assignment_allowed` goes on returning 409.
              // Disabling the switch there leaves a teacher who can neither
              // sequence nor assign, and cannot follow the error telling them
              // to turn sequencing off.
              //
              // The notice below is shown either way: it is the telling that
              // was missing, and it does not depend on the switch position.
              disabled={
                saving ||
                !canEditSequencing ||
                (!curriculumAvailable && !teacherEnabled)
              }
              title={
                !canEditSequencing
                  ? "Only the teacher who owns this class can change its lesson sequencing."
                  : !curriculumAvailable && !teacherEnabled
                    ? "No curriculum is assigned to this organization."
                    : undefined
              }
              className="relative h-8 w-14 shrink-0 rounded-full disabled:opacity-50"
              style={{
                backgroundColor: teacherEnabled ? "#28543D" : "#8F887A",
              }}
            >
              <span
                className="absolute top-1 h-6 w-6 rounded-full bg-white shadow-sm"
                style={{ left: teacherEnabled ? "28px" : "4px" }}
              />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            // Left open deliberately: reading another teacher's pacing is the
            // point of act-as. Save is what gets disabled, inside.
            className="flex w-full items-center justify-between gap-4 border-t border-[#DED8CC] px-5 py-3 text-left font-bold text-[#28543D] hover:bg-[#EEF0E3]"
          >
            <span className="flex items-center gap-2">
              <Settings2 size={16} /> Lesson settings
            </span>
            <span className="text-xs font-semibold">
              {/* No pace target in No expectation mode (R30). */}
              {pointerMode
                ? "No expectation"
                : `${savedWeeklyTotal} expected/week`}
            </span>
          </button>
        </div>
      </header>

      {/* Two independent notices, and the order is deliberate: the read-only
          state governs every control below it, so it reads first. The
          co-teacher notice is transient and dismissable, and can appear
          alongside it -- an org admin viewing a group still wants to know
          somebody changed it. */}
      {!canEditSequencing && (
        // Stated once, up front. A disabled switch on its own reads as a bug;
        // the backend refuses these writes either way, so the only question is
        // whether the admin finds out before or after clicking.
        <div
          role="status"
          className="rounded-[8px] border border-[#F2D9A8] bg-[#FFF8EC] px-5 py-4 text-sm font-semibold text-[#9A5B00]"
        >
          Only the teacher who owns this class can change its lesson sequencing.
          Switch back to your own account to manage your groups.
        </div>
      )}

      {auditLog.length > 0 && (
        <div
          role="status"
          className="flex items-center gap-2.5 rounded-[8px] border px-5 py-3 text-sm font-semibold"
          style={{
            backgroundColor: "#FFF9EE",
            borderColor: "#F0E4CC",
            color: "#7A5B22",
          }}
        >
          <History size={16} aria-hidden="true" className="shrink-0" />
          <span className="min-w-0 truncate">
            {describeSequencingEvent(auditLog[0], formatDayLabel)}
          </span>
          {auditLog.length > 1 && (
            <span
              className="ml-auto shrink-0 rounded-full bg-white px-2.5 py-0.5 text-xs font-bold"
              style={{ border: "1px solid #EAD9B8" }}
            >
              {/* `+` at the cap: `events()` returns a page, not a total, so the
                  array length stops being the history once it fills. A bulk
                  shift writes one event per student, so a class of thirty
                  reaches this in a handful of actions and the pill would sit at
                  a wrong number for good. */}
              {auditLog.length >= EVENT_PAGE_SIZE
                ? `${EVENT_PAGE_SIZE}+`
                : auditLog.length}{" "}
              events
            </span>
          )}
        </div>
      )}

      {/* Off, this panel is only the explainer: the picker exists to scope the
          roster, and there is no roster while sequencing is off. Lesson settings
          stays reachable and still writes per classroom, so with no picker it
          lands on the first selected group -- named in that dialog's header so
          the target is stated rather than assumed.

          The roster still loads, because the starting-point review reads it to
          open over this panel. */}
      <section className="overflow-hidden rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1]">
        {sequencingActive && (
          <div className="sequencing-controls max-md:px-5 max-md:py-6">
            <GroupMultiSelect
              label="Choose groups"
              options={overview.classrooms.map((classroom) => ({
                id: classroom.id,
                name: classroom.name,
              }))}
              values={classroomIds}
              onChange={setGroupIds}
            />
          </div>
        )}

        {sequencingActive ? (
          <>
            {/* One row, as the export has it: the heading, then the actions
                and the search right-aligned beside it. `flex-wrap` on the
                actions is the single deviation -- without it the actions track
                refuses to shrink and takes its width out of the heading column,
                which is a collision rather than a layout. It is invisible
                whenever the row fits. */}
            <div className="student-toolbar bg-[#FBF8F1] px-6 py-5">
              <div className="min-w-0">
                <h2 className="text-xl font-extrabold text-[#294B39]">
                  Students
                </h2>
                <p className="mt-1 text-sm font-semibold text-[#687368]">
                  {displayedClassroomIds.size === overview.classrooms.length
                    ? "All groups"
                    : overview.classrooms
                        .filter((classroom) =>
                          displayedClassroomIds.has(classroom.id),
                        )
                        .map((classroom) => classroom.name)
                        .join(", ")}
                  {search.trim() &&
                    ` · ${visibleStudents.length} of ${rosterStudents.length}`}
                  {!primary.configured && " · Curriculum setup required"}
                </p>
              </div>
              <div className="student-toolbar-actions">
                {selectionSpansGroups && (
                  <p className="text-xs font-bold text-[#9A5B00]">
                    Select students from a single group to change lessons.
                  </p>
                )}
                {/* Available on any day. `change_lesson` takes a `day_offset`
                    for exactly this -- it lands the change on the day the
                    teacher is looking at rather than on today -- so the
                    current-day-only rule that used to sit here was a client
                    restriction the API never asked for, and the hint explaining
                    it was explaining our own limitation. */}
                {selectedStudents.size > 0 && changeTargetClassroomId && (
                  <>
                    <PrimaryButton
                      size="sm"
                      onClick={openChangeModal}
                      disabled={!canEditSequencing}
                    >
                      Change lesson ({selectedStudents.size})
                    </PrimaryButton>
                    {/* Day-level controls, so weekly does not get them --
                        the same rule `canSkipDate` already applies to Skip
                        day. A weekly column is the student's window, drawn
                        from position, and no card on it shows a date; a shift
                        moves `assigned_for_date` on rows this view never
                        renders, so the roster came back identical under a
                        toast saying the schedule had moved. Shown again if
                        weekly ever renders dates.

                        Keyed on the selection's own classroom, not the page's:
                        `changeTargetClassroomId` is null unless every selected
                        student shares one, so this is always that group's mode
                        even when the table mixes two. */}
                    {!isWeeklyClassroom(changeTargetClassroomId) && (
                      <>
                        {/* No expectation mode steps a lesson, not a day (R16),
                            and with no confirmation and no undo (R21): the
                            teacher's latest placement is the truth, and a
                            mistaken move is undone by moving again. */}
                        <button
                          type="button"
                          onClick={() =>
                            pointerMode
                              ? void shiftSchedules("back")
                              : setShiftConfirm("back")
                          }
                          disabled={
                            !canEditSequencing || saving || !canShiftBack
                          }
                          title={
                            canShiftBack
                              ? undefined
                              : pointerMode
                                ? "These students have no earlier lesson to move back to."
                                : // The flag says the move is refused, not why:
                                  // the day it would land on can be full, the
                                  // landing can fall before today, or there can
                                  // be nothing left to move. Naming one of those
                                  // would be wrong in the other two.
                                  "There is no room to move these lessons back a day."
                          }
                          className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-[#28543D] bg-white px-4 text-sm font-bold text-[#28543D] transition-colors hover:bg-[#EEF0E3] disabled:opacity-50"
                        >
                          <ChevronLeft size={15} strokeWidth={2.5} />
                          {pointerMode
                            ? "Move 1 lesson back"
                            : "Move 1 day back"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            pointerMode
                              ? void shiftSchedules("forward")
                              : setShiftConfirm("forward")
                          }
                          disabled={
                            !canEditSequencing || saving || !canShiftForward
                          }
                          title={
                            canShiftForward
                              ? undefined
                              : pointerMode
                                ? "These students have no later lesson to move forward to."
                                : "There is no room to move these lessons forward a day."
                          }
                          className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-[#28543D] bg-white px-4 text-sm font-bold text-[#28543D] transition-colors hover:bg-[#EEF0E3] disabled:opacity-50"
                        >
                          {pointerMode
                            ? "Move 1 lesson forward"
                            : "Move 1 day forward"}
                          <ChevronRight size={15} strokeWidth={2.5} />
                        </button>
                      </>
                    )}
                  </>
                )}
                {!showingToday && (
                  <button
                    type="button"
                    onClick={() => setDayOffset(0)}
                    className="h-10 shrink-0 rounded-lg border border-[#28543D] bg-white px-4 text-sm font-bold text-[#28543D] transition-colors hover:bg-[#EEF0E3]"
                  >
                    Go to current day
                  </button>
                )}
                <label className="student-search block">
                  <span className="sr-only">Search students</span>
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-3 text-[#687368]"
                  />
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search students"
                    className="h-10 w-full rounded-lg border border-[#D6CEBF] pl-10 pr-3 text-sm font-semibold outline-none focus:border-[#28543D]"
                  />
                </label>
              </div>
            </div>

            {/* Paging revalidates in place. The skeleton used to replace this
                whole block -- pager included -- so clicking an arrow removed
                the arrow, which is what PRD-390 reports. Dropping the loading
                state outright would swap that for a click with no feedback at
                all, so the day stays on screen and is dimmed while the next
                one loads. Safe because the column headers come from the same
                response as the rows: both hold the old day together, and
                `rosterRequestRef` already discards out-of-order responses. */}
            <div
              aria-busy={rosterLoading}
              className={`space-y-3 border-t border-[#DED8CC] bg-[#FBF8F1] p-3 transition-opacity ${rosterLoading || rosterStale ? "opacity-50" : ""}`}
            >
              <div className="compact-pager items-center gap-3 px-1">
                {/* Hidden, not disabled. Weekly columns are the student's
                    window, derived from position, and the API refuses a
                    `day_offset` alongside `weekly_view` for that reason -- so
                    there is nothing behind these arrows and a permanently grey
                    chevron just asks the teacher to keep testing it. This is
                    the client half of that decision; the server half already
                    reports `has_earlier`/`has_later` false.

                    Shown while any visible row is daily: a mixed selection
                    renders both shapes in one table, and the daily rows still
                    page. */}
                {anyDailyRoster && (
                  <PagerButton
                    direction="left"
                    onClick={() => setDayOffset((current) => current - 1)}
                    disabled={!canPageBack || rosterLoading}
                  />
                )}
                <div className="grid flex-1 grid-cols-3 gap-2">
                  <DayColumnLabel
                    heading={pointerMode ? "Previous" : undefined}
                    weekly={weeklyRoster}
                    date={primary.previous_date}
                    skip={headerSkip(primary.previous_date)}
                    onClick={
                      canSkipDate(primary.previous_date)
                        ? () => openSkipModal(primary.previous_date)
                        : undefined
                    }
                  />
                  <DayColumnLabel
                    heading={pointerMode ? "Current" : undefined}
                    weekly={weeklyRoster}
                    date={primary.current_date}
                    current={showingToday}
                    skip={headerSkip(primary.current_date)}
                    onClick={
                      canSkipDate(primary.current_date)
                        ? () => openSkipModal(primary.current_date)
                        : undefined
                    }
                  />
                  <DayColumnLabel
                    heading={pointerMode ? "Next" : undefined}
                    weekly={weeklyRoster}
                    date={primary.next_date}
                    skip={headerSkip(primary.next_date)}
                    onClick={
                      canSkipDate(primary.next_date)
                        ? () => openSkipModal(primary.next_date)
                        : undefined
                    }
                  />
                </div>
                {anyDailyRoster && (
                  <PagerButton
                    direction="right"
                    onClick={() => setDayOffset((current) => current + 1)}
                    disabled={!canPageForward || rosterLoading}
                  />
                )}
              </div>

              {/* Desktop follows the design's table: dates are column headers
                    and each row carries its own day pagers, bounded by that
                    student's own window rather than the roster's. Below md the
                    card list above/below keeps the single shared pager, which is
                    the only shape that fits a narrow screen. */}
              <div className="assignment-desktop-table overflow-x-auto">
                {/* `table-fixed`, because the declared column widths were
                    advisory without it. `truncate` sets `white-space: nowrap`,
                    which makes a cell's *min*-content the whole string --
                    `overflow: hidden` does not reduce that contribution -- so
                    auto layout grew the Student column past the `w-[214px]` it
                    asks for and took the difference out of the day columns. A
                    `max-w` on the name only moved the number: 180px demanded
                    252px of column, and removing it demanded 282px. Fixed
                    layout makes the widths authoritative, so the name
                    ellipsizes at the 142px the column actually leaves it and
                    the three day columns come out even instead of 175/244/245.
                    Safe for the day cells: their lesson chips wrap rather than
                    nowrap, so a long title uses another line instead of
                    stretching its column. */}
                <table className="w-full min-w-[980px] table-fixed text-left">
                  <thead>
                    <tr className="text-xs font-bold uppercase tracking-wide text-[#687368]">
                      <th className="w-14 px-5 py-2">
                        <button
                          type="button"
                          role="checkbox"
                          aria-label="Select all visible students"
                          aria-checked={allVisibleSelected}
                          onClick={toggleAllVisible}
                          className={`flex h-6 w-6 items-center justify-center rounded-md border ${allVisibleSelected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
                        >
                          <Check size={14} strokeWidth={3} />
                        </button>
                      </th>
                      <th className="w-[214px] px-3 py-2">Student</th>
                      {anyDailyRoster && <th className="w-12 px-2 py-3" />}
                      <th className="px-3 py-3">
                        <DayColumnLabel
                          heading={pointerMode ? "Previous" : undefined}
                          weekly={weeklyRoster}
                          date={primary.previous_date}
                          skip={headerSkip(primary.previous_date)}
                          onClick={
                            canSkipDate(primary.previous_date)
                              ? () => openSkipModal(primary.previous_date)
                              : undefined
                          }
                        />
                      </th>
                      <th className="px-3 py-3">
                        <DayColumnLabel
                          heading={pointerMode ? "Current" : undefined}
                          weekly={weeklyRoster}
                          date={primary.current_date}
                          current={showingToday}
                          skip={headerSkip(primary.current_date)}
                          onClick={
                            canSkipDate(primary.current_date)
                              ? () => openSkipModal(primary.current_date)
                              : undefined
                          }
                        />
                      </th>
                      <th className="px-3 py-3">
                        <DayColumnLabel
                          heading={pointerMode ? "Next" : undefined}
                          weekly={weeklyRoster}
                          date={primary.next_date}
                          skip={headerSkip(primary.next_date)}
                          onClick={
                            canSkipDate(primary.next_date)
                              ? () => openSkipModal(primary.next_date)
                              : undefined
                          }
                        />
                      </th>
                      {anyDailyRoster && <th className="w-12 px-2 py-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleStudents.map((student) => {
                      const selected = selectedStudents.has(student.student_id);
                      return (
                        <tr
                          key={`${student.classroom_id}-${student.student_id}`}
                          className={selected ? "is-selected" : ""}
                        >
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              role="checkbox"
                              aria-label={`Select ${student.name}`}
                              aria-checked={selected}
                              onClick={() => toggleStudent(student.student_id)}
                              className={`flex h-6 w-6 items-center justify-center rounded-md border ${selected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
                            >
                              <Check size={14} strokeWidth={3} />
                            </button>
                          </td>
                          <td className="px-3 py-4">
                            {/* `flex-1` alongside `min-w-0`, and no fixed cap
                                on the name. The column is `w-[214px] px-3`, so
                                it has 190px of content and the avatar plus its
                                gap take 48 of them; a `min-w-0` div with no
                                `flex-1` sizes to its content instead, so the
                                old `max-w-[180px]` name asked for a 252px
                                column and the day columns lost the difference.
                                The width is only authoritative because of
                                `table-fixed` on the table -- see the note
                                there -- and with both, `truncate` cuts at the
                                142px the column actually leaves, which is an
                                ellipsis a character cap cannot place: it has
                                no idea how wide the column is. `title` carries
                                the full name either way. */}
                            <div className="flex items-center gap-3">
                              <Avatar name={student.name} selected={selected} />
                              <div className="min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedStudents(
                                      new Set([student.student_id]),
                                    )
                                  }
                                  title={student.name}
                                  className="block w-full truncate text-left font-extrabold text-[#294B39] hover:underline"
                                >
                                  {student.name}
                                </button>
                                {/* `title` for the same reason the name button
                                    above carries one: once the label is
                                    single-line and clipped, two groups sharing
                                    a prefix render identically and the row
                                    stops telling the teacher which one it is.
                                    Wrapping would answer it too, but overflow
                                    staying on one line is the product decision
                                    here, and a wrapped group name makes row
                                    heights uneven down the column. */}
                                <p
                                  title={student.classroom_name}
                                  className="truncate text-xs font-bold text-[#687368]"
                                >
                                  {student.classroom_name}
                                </p>
                                {/* Amber pill rather than grey text appended to
                                      the group name: this is the state the teacher
                                      has to act on, and it was reading as part of
                                      the label. The tooltip carries
                                      `pending_reason`, which separates a missing
                                      grade from a grade this curriculum does not
                                      cover -- different fixes, by different
                                      people. */}
                                {/* Past the last lesson is not a placement to
                                    fix in No expectation mode -- Next reads No
                                    lessons (R10). */}
                                {student.status !== "active" &&
                                  !(
                                    pointerMode &&
                                    student.status === "exhausted"
                                  ) && (
                                    <span
                                      className="mt-1 inline-block rounded-full bg-[#FFF4E5] px-2 py-0.5 text-xs font-bold text-[#9A5B00]"
                                      title={
                                        student.pending_reason ??
                                        "Needs a starting lesson"
                                      }
                                    >
                                      Needs placement
                                    </span>
                                  )}
                              </div>
                            </div>
                          </td>
                          {/* The cell tracks the header spacer so the three
                              lesson columns stay aligned; the button inside
                              tracks this row's own classroom. A mixed
                              selection renders both shapes at once, so a
                              weekly row gets an empty cell next to a daily
                              row's chevron rather than the whole column
                              disappearing. */}
                          {anyDailyRoster && (
                            <td className="px-2 py-4">
                              {!pointerMode &&
                                !isWeeklyClassroom(student.classroom_id) && (
                                  <PagerButton
                                    direction="left"
                                    onClick={() =>
                                      setDayOffset((current) => current - 1)
                                    }
                                    disabled={
                                      !student.has_earlier || rosterLoading
                                    }
                                  />
                                )}
                            </td>
                          )}
                          <td className="px-3 py-4">
                            <div className="min-h-[72px] rounded-lg border border-[#DED8CC] bg-[#F3EDE2] p-2.5">
                              <LessonList
                                withPath={pointerMode}
                                emptyLabel={previousEmptyLabel}
                                lessons={student.previous}
                              />
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            {/* Tinted on the current day, as the export draws
                                  it -- the green header and the cells under it
                                  are one column, and leaving the cells neutral
                                  made the header look like a stray highlight. */}
                            <button
                              type="button"
                              onClick={() =>
                                openChangeForStudent(student.student_id)
                              }
                              aria-label={`Change current lesson for ${student.name}`}
                              className="min-h-[72px] w-full rounded-lg border p-2.5 text-left transition-colors hover:border-[#28543D]"
                              style={
                                showingToday
                                  ? {
                                      borderColor: "#9AA884",
                                      backgroundColor: "#EEF0E3",
                                    }
                                  : {
                                      borderColor: "#DED8CC",
                                      backgroundColor: "#F3EDE2",
                                    }
                              }
                            >
                              <LessonList
                                withPath={pointerMode}
                                lessons={student.current}
                                emptyLabel={rosterEmptyLabel}
                              />
                            </button>
                          </td>
                          <td className="px-3 py-4">
                            <div className="min-h-[72px] rounded-lg border border-[#DED8CC] bg-[#F3EDE2] p-2.5">
                              <LessonList
                                withPath={pointerMode}
                                emptyLabel={rosterEmptyLabel}
                                lessons={student.upcoming}
                              />
                            </div>
                          </td>
                          {anyDailyRoster && (
                            <td className="px-2 py-4">
                              {!pointerMode &&
                                !isWeeklyClassroom(student.classroom_id) && (
                                  <PagerButton
                                    direction="right"
                                    onClick={() =>
                                      setDayOffset((current) => current + 1)
                                    }
                                    disabled={
                                      !student.has_later || rosterLoading
                                    }
                                  />
                                )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {!visibleStudents.length && (
                      <tr>
                        <td
                          colSpan={7}
                          className="border-t border-[#DED8CC] px-5 py-12 text-center text-sm font-semibold text-[#687368]"
                        >
                          {search.trim()
                            ? `No students match “${search.trim()}”.`
                            : "No students in this view."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="assignment-compact-list">
                {visibleStudents.length ? (
                  visibleStudents.map((student) => (
                    <div
                      key={`${student.classroom_id}-${student.student_id}`}
                      className="rounded-[6px] border border-[#DED8CC] bg-[#FBF8F1] p-4"
                    >
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          role="checkbox"
                          aria-label={`Select ${student.name}`}
                          aria-checked={selectedStudents.has(
                            student.student_id,
                          )}
                          onClick={() => toggleStudent(student.student_id)}
                          className={`mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${selectedStudents.has(student.student_id) ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#9AA884] bg-white text-transparent"}`}
                        >
                          <Check size={14} strokeWidth={3} />
                        </button>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <Avatar
                                name={student.name}
                                selected={selectedStudents.has(
                                  student.student_id,
                                )}
                              />
                              {/* Same invariant as the table row above: shrink
                                  inside the container and ellipsize, never
                                  widen the row. This half carried no
                                  truncation at all, so a long name pushed the
                                  card's own contents sideways. */}
                              <div className="min-w-0">
                                {/* Spec: roster rows show last names only, and
                                    clicking a name selects just that student. */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    setSelectedStudents(
                                      new Set([student.student_id]),
                                    )
                                  }
                                  title={student.name}
                                  className="block w-full truncate text-left font-extrabold text-[#294B39] hover:underline"
                                >
                                  {student.name}
                                </button>
                                {/* `title` for the same reason the name button
                                    above carries one: once the label is
                                    single-line and clipped, two groups sharing
                                    a prefix render identically and the row
                                    stops telling the teacher which one it is.
                                    Wrapping would answer it too, but overflow
                                    staying on one line is the product decision
                                    here, and a wrapped group name makes row
                                    heights uneven down the column. */}
                                <p
                                  title={student.classroom_name}
                                  className="truncate text-xs font-bold text-[#687368]"
                                >
                                  {student.classroom_name}
                                </p>
                                {/* Amber pill rather than grey text appended to
                                      the group name: this is the state the teacher
                                      has to act on, and it was reading as part of
                                      the label. The tooltip carries
                                      `pending_reason`, which separates a missing
                                      grade from a grade this curriculum does not
                                      cover -- different fixes, by different
                                      people. */}
                                {/* Past the last lesson is not a placement to
                                    fix in No expectation mode -- Next reads No
                                    lessons (R10). */}
                                {student.status !== "active" &&
                                  !(
                                    pointerMode &&
                                    student.status === "exhausted"
                                  ) && (
                                    <span
                                      className="mt-1 inline-block rounded-full bg-[#FFF4E5] px-2 py-0.5 text-xs font-bold text-[#9A5B00]"
                                      title={
                                        student.pending_reason ??
                                        "Needs a starting lesson"
                                      }
                                    >
                                      Needs placement
                                    </span>
                                  )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-3">
                            <div className="rounded-lg border border-[#DED8CC] bg-[#F3EDE2] p-2">
                              <LessonList
                                withPath={pointerMode}
                                emptyLabel={previousEmptyLabel}
                                lessons={student.previous}
                              />
                            </div>
                            {/* Mirrors the desktop table: below 1100px that
                                  one is display:none and this renders instead,
                                  so it carries the same tint and the same
                                  always-available override. */}
                            <button
                              type="button"
                              onClick={() =>
                                openChangeForStudent(student.student_id)
                              }
                              aria-label={`Change current lesson for ${student.name}`}
                              className="rounded-lg border p-2 text-left transition-colors hover:border-[#28543D]"
                              style={
                                showingToday
                                  ? {
                                      borderColor: "#9AA884",
                                      backgroundColor: "#EEF0E3",
                                    }
                                  : {
                                      borderColor: "#DED8CC",
                                      backgroundColor: "#F3EDE2",
                                    }
                              }
                            >
                              <LessonList
                                withPath={pointerMode}
                                lessons={student.current}
                                emptyLabel={rosterEmptyLabel}
                              />
                            </button>
                            <div className="rounded-lg border border-[#DED8CC] bg-[#F3EDE2] p-2">
                              <LessonList
                                withPath={pointerMode}
                                emptyLabel={rosterEmptyLabel}
                                lessons={student.upcoming}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="rounded-[6px] border border-[#DED8CC] bg-[#FBF8F1] px-5 py-12 text-center text-sm font-semibold text-[#687368]">
                    {search.trim()
                      ? `No students match “${search.trim()}”.`
                      : "No students in this view."}
                  </p>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="border-t border-[#DED8CC] px-10 py-12 text-center max-md:px-5 max-md:py-10">
            {/* Two reasons reach this panel and they need different words. With
                no curriculum the switch is not the remedy -- it may well
                already be on -- so pointing at it would be the same dead-end
                advice the assignment error used to give.

                The missing-curriculum sentence lives here rather than in a
                notice card of its own above. One page cannot explain the same
                thing twice, and this panel is already the thing a teacher reads
                when the grid is absent: putting the explanation anywhere else
                left the panel saying only that nothing is broken, which is not
                what they came to find out. */}
            <h2 className="text-lg font-extrabold text-[#294B39]">
              {curriculumAvailable
                ? "Auto-sequencing is off"
                : "Auto-sequencing is unavailable"}
            </h2>
            <p className="mx-auto mt-2 max-w-[46ch] text-sm font-semibold leading-6 text-[#687368]">
              {curriculumAvailable ? (
                "Turn it on with the switch above to build each student's schedule. Your groups and students are unchanged, and lessons you assign by hand keep working."
              ) : (
                <>
                  No curriculum found. Auto sequencing is disabled. Contact{" "}
                  <a
                    href="mailto:outreach@evertutor.ai"
                    className="font-bold text-[#28543D] underline hover:no-underline"
                  >
                    outreach@evertutor.ai
                  </a>{" "}
                  for any help regarding this issue.
                </>
              )}
            </p>
          </div>
        )}
      </section>

      {error && (
        <div
          role="alert"
          className="flex items-center justify-between rounded-[6px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          <span>{error}</span>
          <button
            type="button"
            aria-label="Dismiss error"
            onClick={() => setError(null)}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {settingsOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lesson-settings-title"
        >
          <div className="mx-4 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[#DED8CC] bg-[#FBF8F1] px-6 py-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-[#28543D]">
                  Settings
                </p>
                <h2
                  id="lesson-settings-title"
                  className="mt-1 text-2xl font-bold text-[#294B39]"
                >
                  Lesson settings
                </h2>
                {/* No group selector. Lesson settings are teacher-wide: one
                    pace for every group, so there is nothing here to choose
                    between. The control this replaces existed because the save
                    went to a single classroom and, with the roster's group
                    picker hidden, nothing else could say which -- a teacher
                    with several groups could otherwise pace only the first.
                    That reason is gone with the per-group write. */}
                <p className="mt-1 text-sm font-semibold text-[#687368]">
                  {settingsScopeLabel}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close settings"
                onClick={() => setSettingsOpen(false)}
                className="h-9 w-9 shrink-0 rounded-full bg-[#F3EDE2] text-2xl leading-none text-[#687368]"
              >
                ×
              </button>
            </div>
            <div className="space-y-6 p-6">
              {/* `disabled` on the fieldset, not on each button. Native
                  fieldset[disabled] cascades to every descendant control, so
                  a control added inside later cannot miss it -- which is how
                  these pace buttons stayed clickable in read-only mode while
                  Save was greyed out. */}
              <fieldset
                className={`grid gap-2 ${canEditSequencing ? "" : "opacity-50"}`}
                disabled={!canEditSequencing}
              >
                <legend className="text-sm font-bold text-[#294B39]">
                  Expected lessons per week
                </legend>
                <p className="text-sm text-[#687368]">
                  {draftPointer
                    ? "No pace target. Students move only by finishing."
                    : "Used to report whether a student is moving faster or slower than expected."}
                </p>
                {/* The design asks one question first — batch or daily rate —
                    and only then the number, instead of two numeric pickers
                    the teacher has to multiply in their head. No expectation
                    mode is the first of three when offered (R1): a mode of its
                    own, not a weekly number that happens to be zero. */}
                <div
                  className={`grid gap-3 ${offerNoExpectation ? "grid-cols-3" : "grid-cols-2"}`}
                >
                  {offerNoExpectation && (
                    <button
                      type="button"
                      aria-pressed={draftPointer}
                      onClick={() => setAssignmentMode("none")}
                      className="rounded-[6px] border px-3 py-3 font-bold"
                      style={{
                        backgroundColor: draftPointer ? "#28543D" : "#fff",
                        borderColor: "#28543D",
                        color: draftPointer ? "#fff" : "#28543D",
                      }}
                    >
                      No expectation
                    </button>
                  )}
                  {(
                    [
                      // The design's labels (Alloy, teacher v12; P-PRD-525
                      // section 9 lists them as settled). They name the two
                      // ways of reaching a pace -- a weekly batch of up to
                      // five, or a daily rate -- not two ranges of one total,
                      // which is why the mode is stored rather than derived
                      // from the total. See `isWeeklyAssignment`.
                      [true, "5 or fewer"],
                      [false, "More than 5"],
                    ] as [boolean, string][]
                  ).map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={!draftPointer && weeklyMode === value}
                      onClick={() => {
                        setAssignmentMode(value ? "weekly" : "daily");
                        // A weekly batch has no daily cap; the schema has no
                        // field for that, so it is stored as one a day.
                        setDaily(value ? 1 : Math.max(2, daily));
                        // And the weekly draft is clamped on the way in, so
                        // switching branches without touching a number cannot
                        // save a 10- or 15-lesson week under a "5 or fewer"
                        // label. The daily branch keeps its own draft in
                        // `daily`, so nothing is lost switching back.
                        if (value) {
                          setWeekly((current) =>
                            Math.min(current, MAX_DAYS_PER_WEEK),
                          );
                        }
                      }}
                      className="rounded-[6px] border px-3 py-3 font-bold"
                      style={{
                        backgroundColor:
                          !draftPointer && weeklyMode === value
                            ? "#28543D"
                            : "#fff",
                        borderColor: "#28543D",
                        color:
                          !draftPointer && weeklyMode === value
                            ? "#fff"
                            : "#28543D",
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
              <div
                className="rounded-[6px] border px-4 py-3 text-sm"
                style={{
                  backgroundColor:
                    weeklyMode || draftPointer ? "#EEF0E3" : "#FBF8F1",
                  borderColor:
                    weeklyMode || draftPointer ? "#9AA884" : "#DED8CC",
                }}
              >
                <span className="block font-bold text-[#294B39]">
                  {draftPointer
                    ? "No expectation"
                    : weeklyMode
                      ? "Weekly assignment"
                      : "Daily assignment"}
                </span>
                <span className="mt-1 block text-sm leading-5 text-[#687368]">
                  {/* The explainer's text is approved as written (R1). */}
                  {draftPointer
                    ? "Students get one lesson at a time and move on only when they finish it. Nothing is scheduled against a date, and nothing is ever skipped. If you move a student back, they work through those lessons again, including ones they already finished. No expectation always works in Completion mode."
                    : weeklyMode
                      ? `${weeklyTotal} ${weeklyTotal === 1 ? "lesson" : "lessons"} will be assigned per week, so students follow a weekly sequence with no daily cap.`
                      : "Students follow a daily sequence. Choose the expected number of lessons per day below."}
                </span>
              </div>
              {/* Weekly mode picks the week's total; daily mode picks the
                  per-day cap. One control each, never both, because the two
                  multiply into the same stored pair and a teacher shown both
                  would be setting one number twice. */}
              {!draftPointer && weeklyMode && (
                <fieldset
                  className={`grid gap-2 ${canEditSequencing ? "" : "opacity-50"}`}
                  disabled={!canEditSequencing}
                >
                  <div className="flex items-start justify-between gap-4">
                    <legend className="text-sm font-bold text-[#294B39]">
                      Lessons per week
                    </legend>
                    <span className="shrink-0 rounded-full bg-[#EEF0E3] px-3 py-1 text-xs font-bold text-[#28543D]">
                      {weeklyTotal}/week
                    </span>
                  </div>
                  <p className="text-sm text-[#687368]">
                    Set the weekly pace target for the weekly sequence.
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from(
                      { length: MAX_DAYS_PER_WEEK },
                      (_, index) => index + 1,
                    ).map((count) => (
                      <button
                        key={count}
                        type="button"
                        aria-pressed={weekly === count}
                        onClick={() => setWeekly(count)}
                        className="rounded-[6px] border px-3 py-3 font-bold"
                        style={{
                          backgroundColor:
                            weekly === count ? "#28543D" : "#fff",
                          borderColor: "#28543D",
                          color: weekly === count ? "#fff" : "#28543D",
                        }}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-[#687368]">
                    Students receive up to {weekly}{" "}
                    {weekly === 1 ? "lesson" : "lessons"} across the week with
                    no daily cap.
                  </p>
                </fieldset>
              )}
              {/* Only in daily mode. A weekly batch has no daily cap to set,
                  which is the whole distinction the control above draws. */}
              {!draftPointer && !weeklyMode && (
                <fieldset
                  className={`grid gap-2 ${canEditSequencing ? "" : "opacity-50"}`}
                  disabled={!canEditSequencing}
                >
                  <div className="flex items-start justify-between gap-4">
                    <legend className="text-sm font-bold text-[#294B39]">
                      Expected lessons per day
                    </legend>
                    <span className="shrink-0 rounded-full bg-[#EEF0E3] px-3 py-1 text-xs font-bold text-[#28543D]">
                      {weeklyTotal}/week
                    </span>
                  </div>
                  <p className="text-sm text-[#687368]">
                    Set the daily pace target for the daily sequence.
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {Array.from(
                      { length: MAX_LESSONS_PER_DAY },
                      (_, index) => index + 1,
                    ).map((count) => (
                      <button
                        key={count}
                        type="button"
                        aria-pressed={daily === count}
                        onClick={() => setDaily(count)}
                        className="rounded-[6px] border px-3 py-3 font-bold"
                        style={{
                          backgroundColor: daily === count ? "#28543D" : "#fff",
                          borderColor: "#28543D",
                          color: daily === count ? "#fff" : "#28543D",
                        }}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-[#687368]">
                    Students receive up to {daily}{" "}
                    {daily === 1 ? "lesson" : "lessons"} per day as their daily
                    pace target.
                  </p>
                </fieldset>
              )}
              {/* The pacing copy is per week and switches *below* five, per the
                  design — see isWeeklyMode. The two controls above stay the
                  source of truth, so this states the resulting weekly pace
                  rather than adding a third editable number that could
                  disagree with them. */}
              {/* Hidden, not disabled, in No expectation mode: it is always
                  completion-gated, so there is no choice to show (R1). */}
              {!draftPointer && (
                <fieldset
                  className={`grid gap-2 ${primary.settings.enabled && canEditSequencing ? "" : "opacity-50"}`}
                  disabled={!canEditSequencing}
                >
                  <legend className="font-bold text-[#294B39]">
                    When a lesson is incomplete
                  </legend>
                  <p className="text-sm text-[#687368]">
                    Choose how lesson progression should work.
                  </p>
                  {!canEditSequencing && (
                    <p className="text-xs font-semibold text-[#9A5B00]">
                      Read-only: only the teacher who owns this class can change
                      it.
                    </p>
                  )}
                  {!primary.settings.enabled && (
                    <p className="text-xs font-semibold text-[#9A5B00]">
                      Auto-sequencing is off, so lesson progression is not in
                      use yet. These settings save either way.
                    </p>
                  )}
                  {/* Cards without a visible control, as the design has them, but
                    driven by real radios kept off screen. role="radio" on a button
                    supplied the semantics and none of the behaviour: no arrow-key
                    selection, and two tab stops instead of one. */}
                  <div className="grid auto-rows-fr items-stretch gap-2 sm:grid-cols-2">
                    {(["completion", "pacing"] as Mode[]).map((value) => {
                      // Locked whether or not it is the teacher's current value.
                      // Selecting the option already selected is a no-op, so
                      // leaving it live for them would buy nothing and would let
                      // a teacher toggle away and back, which is the thing being
                      // held. Their stored value is untouched either way.
                      const locked = !pacingModeEnabled && value === "pacing";
                      const selectable =
                        primary.settings.enabled &&
                        canEditSequencing &&
                        !locked;
                      return (
                        <label
                          key={value}
                          className={`flex h-full flex-col rounded-[6px] border p-4 focus-within:ring-2 focus-within:ring-[#28543D] focus-within:ring-offset-1 ${selectable ? "cursor-pointer" : "cursor-not-allowed"} ${locked && mode !== value ? "opacity-60" : ""} ${mode === value ? "border-[#28543D] bg-[#EEF0E3]" : "border-[#DED8CC] bg-white"}`}
                        >
                          <input
                            type="radio"
                            name="incomplete-mode"
                            value={value}
                            checked={mode === value}
                            disabled={
                              !primary.settings.enabled ||
                              !canEditSequencing ||
                              locked
                            }
                            onChange={() => setMode(value)}
                            className="sr-only"
                          />
                          <span className="block font-bold text-[#294B39]">
                            {value === "completion"
                              ? "Completion mode"
                              : "Pacing mode"}
                          </span>
                          <span className="mt-1 block text-sm leading-5 text-[#687368]">
                            {value === "completion"
                              ? "The student gets the next lesson only after completing the current one, even if this means falling slightly behind pace."
                              : "The student gets the next lesson even if the current one is unfinished, to keep them on pace."}
                          </span>
                          {/* Only for a teacher already on pacing, who needs to
                            know their setting is kept and that leaving it is
                            one-way. The greyed card speaks for itself for
                            everyone else. */}
                          {locked && mode === value && (
                            <span className="mt-2 block text-xs font-semibold text-[#9A5B00]">
                              Currently in use. This option is turned off for
                              now, so moving to Completion mode cannot be undone
                              here.
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              )}
            </div>
            <div className="flex flex-col gap-4 border-t border-[#DED8CC] bg-[#FBF8F1] px-6 py-4">
              {/* The design's footer note, shown only when Save will open the
                  starting-point review: into or out of No expectation (R2,
                  R37). A switch between the two paces saves directly. */}
              {draftSwitchesMode && (
                <p className="text-sm font-semibold text-[#687368]">
                  Saving this will ask you to set a starting lesson for every
                  student in the group, including students who already have one.
                </p>
              )}
              {dialogError && <DialogError message={dialogError} />}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-3 font-bold text-[#687368]"
                >
                  Cancel
                </button>
                <PrimaryButton
                  onClick={() => void saveSettings()}
                  disabled={saving || !canEditSequencing}
                >
                  {saving ? "Saving…" : dialogError ? "Try again" : "Save"}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {reviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="activation-title"
        >
          {/* The design's shape: a tall panel with a fixed header and footer and
              only the middle scrolling, so the sign-off and the actions stay
              reachable however long the roster is. */}
          {/* Width note: the export writes max-w-5xl / max-w-6xl, but its
                Tailwind build does not generate those utilities — they compute
                to `none`, so both modals render full width (measured 1648px at
                a 1680px viewport). Copying the class produced a 1024px panel
                that looked nothing like the design. Matching the render. */}
          <div
            className="mx-4 flex w-full flex-col overflow-hidden rounded-[8px] border border-[#DED8CC] bg-[#FBF8F1] shadow-2xl"
            style={{ height: "92vh", maxHeight: "92vh" }}
          >
            <div className="flex items-start justify-between gap-4 border-b border-[#DED8CC] bg-[#FBF8F1] px-6 py-5">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-wide text-[#28543D]">
                  Required review
                </p>
                <h2
                  id="activation-title"
                  className="mt-1 text-2xl font-extrabold text-[#294B39]"
                >
                  Approve student starting points
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#687368]">
                  {/* A switch uses the design's review copy (Alloy, teacher
                      v12, StartingPointsReviewModal); activation keeps its
                      own. */}
                  {/* With sequencing off the switch sets the mode and places
                      no one in front of a lesson until it is turned on, so
                      the copy does not promise to turn it on. */}
                  {modeSwitch
                    ? teacherEnabled
                      ? "Each student already has a suggested starting lesson based on their current position. Change any you need, then approve to turn on Auto-sequencing."
                      : "Each student already has a suggested starting lesson based on their current position. Change any you need, then approve. Students start once Auto-sequencing is on."
                    : pointerReview
                      ? "Review each student’s current lesson before turning Auto-sequencing on. Unchanged placements and completed sequences are preserved. Students without a starting lesson remain waiting for placement."
                      : "Auto-sequencing will remain off until every student has a reviewed starting lesson. Suggested lessons are based on each student’s current position."}
                </p>
                {/* Leaving No expectation only (R37; product, Oct 13): a
                    student part-way through redoing lessons simply stops being
                    asked to, and the teacher should know before approving. */}
                {modeSwitch && !isPointerMode(modeSwitch.target) && (
                  <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-[#294B39]">
                    Any reteach in progress ends: students who were redoing
                    lessons stop being asked to redo them.
                  </p>
                )}
              </div>
              <button
                type="button"
                aria-label="Close activation review"
                disabled={saving}
                onClick={() => setReviewOpen(false)}
                className="h-9 w-9 shrink-0 rounded-full bg-[#F3EDE2] text-2xl leading-none text-[#687368]"
              >
                ×
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 gap-4 border-b border-[#DED8CC] px-6 py-4 md:grid-cols-2">
                <label className="flex min-w-0 flex-1 flex-col gap-2 text-sm font-bold text-[#294B39]">
                  Search students
                  <input
                    value={reviewSearch}
                    onChange={(event) => setReviewSearch(event.target.value)}
                    placeholder="Search by name"
                    className="h-11 rounded-[6px] border border-[#D6CEBF] bg-white px-4 font-normal outline-none focus:border-[#28543D]"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm font-bold text-[#294B39]">
                  Group
                  <select
                    value={reviewGroup}
                    onChange={(event) => setReviewGroup(event.target.value)}
                    className="h-11 rounded-[6px] border border-[#D6CEBF] bg-white px-4 font-semibold outline-none"
                  >
                    <option>All groups</option>
                    {reviewGroups.map((group) => (
                      <option key={group}>{group}</option>
                    ))}
                  </select>
                </label>
                <div
                  className="flex flex-col gap-2 text-sm font-bold text-[#294B39]"
                  style={{ gridColumn: "1 / -1" }}
                >
                  <span>Bulk starting lesson</span>
                  <div className="flex gap-2">
                    <select
                      aria-label="Bulk starting lesson"
                      value={bulkNode ?? ""}
                      onChange={(event) =>
                        setBulkNode(Number(event.target.value) || null)
                      }
                      className="h-11 min-w-56 flex-1 rounded-[6px] border border-[#D6CEBF] bg-white px-3 font-semibold outline-none"
                    >
                      <option value="">Choose starting lesson</option>
                      {bulkOptions.map((option) => (
                        <option
                          key={option.curriculum_node_id}
                          value={option.curriculum_node_id}
                        >
                          {truncateMiddle(
                            formatPath(option.path) || option.title,
                            68,
                          )}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={
                        saving || bulkTargetKeys.length === 0 || !bulkNode
                      }
                      onClick={applyBulkLesson}
                      className="h-11 rounded-[6px] bg-[#28543D] px-4 font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Apply ({bulkTargetKeys.length})
                    </button>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] border-collapse text-left">
                  <thead className="sticky top-0 z-10 bg-[#FBF8F1] text-xs font-extrabold uppercase tracking-wide text-[#687368]">
                    <tr className="border-b border-[#DED8CC]">
                      <th className="w-14 px-5 py-3">
                        <button
                          type="button"
                          role="checkbox"
                          aria-label="Select visible students"
                          aria-checked={reviewVisibleSelected}
                          onClick={() =>
                            setReviewSelected(
                              reviewVisibleSelected
                                ? new Set()
                                : new Set(
                                    reviewStudents.map((student) =>
                                      rowKey(student),
                                    ),
                                  ),
                            )
                          }
                          className={`flex h-6 w-6 items-center justify-center rounded-md border ${reviewVisibleSelected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
                        >
                          <Check size={14} strokeWidth={3} />
                        </button>
                      </th>
                      <th className="px-3 py-3">Student</th>
                      <th className="px-3 py-3">Approved starting point</th>
                      <th className="w-28 px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewStudents.map((student) => {
                      // The same answer the row was seeded with, so the badge
                      // and Reset agree with it -- a switch's suggestion is
                      // not the stored placement (see `reviewSuggestion`).
                      const suggested = reviewSuggestion(
                        student,
                        modeSwitch?.target,
                        pointerReview,
                      );
                      const approved = selections[rowKey(student)] ?? null;
                      const studentOptions =
                        reviewOptionsByClassroom.get(student.classroom_id) ??
                        [];
                      const approvedOption = studentOptions.find(
                        (option) => option.curriculum_node_id === approved,
                      );
                      const retainedCurrent =
                        pointerReview &&
                        !reviewEdited.has(rowKey(student)) &&
                        student.current_lesson?.curriculum_node_id ===
                          approved &&
                        student.current_lesson.lesson_id !==
                          approvedOption?.lesson_id
                          ? student.current_lesson
                          : null;
                      // Which state this row is in, and whether Reset has
                      // anything to undo. Both are in `lessonSequencingRules`
                      // so they can be tested -- see there for why a placement
                      // approved in an earlier review reads as the teacher's
                      // choice rather than the system's suggestion, and why
                      // Reset has to reach a row that opened empty.
                      const placement = startingPointBadge({
                        selected: approved,
                        suggested,
                        isOffered: Boolean(approvedOption),
                        placementStatus: student.placement_status,
                      });
                      const resettable = canResetStartingPoint(
                        approved,
                        suggested,
                      );
                      // A committed move cannot be undone to an empty or
                      // unavailable seed through the change-lesson API.
                      const resetUnavailable =
                        reviewCommitted[rowKey(student)] !== undefined &&
                        !studentOptions.some(
                          (option) => option.curriculum_node_id === suggested,
                        );
                      // A seeded starting point can name a node this
                      // classroom's option list does not offer -- the row is
                      // seeded from `suggested_curriculum_node_id`, while the
                      // options come from the sequence's publishable items, and
                      // the two diverge once a version is retired or the
                      // classroom is re-bound. With no matching `<option>` the
                      // select fell back to its empty placeholder, so the row
                      // read "Choose starting lesson" while the badge beside it
                      // said "Approved" and the footer counted it as valid.
                      //
                      // Name it from the student's own rows where possible, so
                      // the teacher sees which lesson is approved rather than a
                      // control that looks untouched.
                      const approvedElsewhere =
                        approved && !approvedOption
                          ? [
                              ...(student.current ?? []),
                              ...(student.previous ?? []),
                            ].find(
                              (lesson) =>
                                lesson.curriculum_node_id === approved,
                            )
                          : undefined;
                      // Always carries the marker, including when the lesson
                      // could be named: the dropdown should not read like an
                      // ordinary choice when picking it is what the API refuses.
                      const approvedLabel = approvedElsewhere
                        ? `${formatPath(approvedElsewhere.path) || approvedElsewhere.title} (not in this sequence)`
                        : "Current starting point (not in this sequence)";
                      const rowSelected = reviewSelected.has(rowKey(student));
                      return (
                        <tr
                          // Composite, as the two roster tables above already
                          // key these same rows. `reviewRoster` flattens every
                          // classroom into one list, so a student in two groups
                          // appears twice with one `student_id`: duplicate keys,
                          // and React then reconciles the filtered list against
                          // the wrong siblings -- rows for students the search
                          // had excluded kept painting, while selection and
                          // "Apply (n)" counted only the real matches.
                          key={`${student.classroom_id}-${student.student_id}`}
                          className="border-b border-[#DED8CC]"
                        >
                          <td className="px-5 py-4">
                            <button
                              type="button"
                              role="checkbox"
                              aria-label={`Select ${student.name}`}
                              aria-checked={rowSelected}
                              onClick={() =>
                                setReviewSelected((current) => {
                                  const next = new Set(current);
                                  const key = rowKey(student);
                                  if (next.has(key)) next.delete(key);
                                  else next.add(key);
                                  return next;
                                })
                              }
                              className={`flex h-6 w-6 items-center justify-center rounded-md border ${rowSelected ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#D6CEBF] bg-white text-transparent"}`}
                            >
                              <Check size={14} strokeWidth={3} />
                            </button>
                          </td>
                          <td className="px-3 py-4">
                            <p className="font-extrabold text-[#28543D]">
                              {student.name}
                            </p>
                            <p className="mt-1 text-xs text-[#687368]">
                              {student.classroom_name}
                            </p>
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-2">
                              <select
                                aria-label={`Starting lesson for ${student.name}`}
                                // Frozen while the activation is in flight. It
                                // posts one request per group and stops at the
                                // first failure with the modal open, and each
                                // committed group drops its edit marks as it
                                // returns -- so a row edited mid-flight would
                                // have its mark cleared by the request that had
                                // already been built without it, and the retry
                                // would send the new choice as an untouched
                                // seed.
                                disabled={saving}
                                value={approved ?? ""}
                                onChange={(event) => {
                                  setSelections((current) => ({
                                    ...current,
                                    [rowKey(student)]: Number(
                                      event.target.value,
                                    ),
                                  }));
                                  setReviewEdited((current) =>
                                    new Set(current).add(rowKey(student)),
                                  );
                                }}
                                title={
                                  retainedCurrent
                                    ? retainedCurrent.title
                                    : approvedOption
                                      ? formatPath(approvedOption.path)
                                      : approved
                                        ? approvedLabel
                                        : undefined
                                }
                                className="h-11 min-w-0 flex-1 rounded-[6px] border border-[#D6CEBF] bg-white px-3 text-sm font-semibold outline-none focus:border-[#28543D]"
                              >
                                <option value="">Choose starting lesson</option>
                                {approved && !approvedOption && (
                                  <option value={approved}>
                                    {truncateMiddle(approvedLabel, 58)}
                                  </option>
                                )}
                                {studentOptions.map((option) => (
                                  <option
                                    key={option.curriculum_node_id}
                                    value={option.curriculum_node_id}
                                  >
                                    {truncateMiddle(
                                      retainedCurrent &&
                                        option.curriculum_node_id === approved
                                        ? `${retainedCurrent.title} (current version)`
                                        : formatPath(option.path) ||
                                            option.title,
                                      58,
                                    )}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                aria-label={`Reset starting point for ${student.name}`}
                                disabled={
                                  saving || !resettable || resetUnavailable
                                }
                                title={
                                  resetUnavailable
                                    ? "Starting lesson already saved. Choose another available lesson to change it."
                                    : undefined
                                }
                                onClick={() => {
                                  setSelections((current) => {
                                    const next = { ...current };
                                    const restored =
                                      resetStartingPoint(suggested);
                                    // Cleared rather than set, for a row the
                                    // system had no suggestion for: the
                                    // seeding effect only fills blanks it has
                                    // a suggestion for, so this stays empty.
                                    if (restored === undefined) {
                                      delete next[rowKey(student)];
                                    } else {
                                      next[rowKey(student)] = restored;
                                    }
                                    return next;
                                  });
                                  // Reset still means the original suggestion.
                                  // If a failed enable already persisted another
                                  // pick, returning to the seed is a new write.
                                  setReviewEdited((current) => {
                                    const next = new Set(current);
                                    const key = rowKey(student);
                                    if (
                                      reviewCommitted[key] !== undefined &&
                                      reviewCommitted[key] !== suggested
                                    ) {
                                      next.add(key);
                                    } else {
                                      next.delete(key);
                                    }
                                    return next;
                                  });
                                }}
                                className="h-11 shrink-0 rounded-[6px] border border-[#D6CEBF] bg-white px-3 text-xs font-bold text-[#28543D] disabled:cursor-not-allowed disabled:opacity-35"
                              >
                                Reset
                              </button>
                            </div>
                            {resetUnavailable && (
                              <p className="mt-1 text-xs text-[#687368]">
                                Starting lesson already saved. Choose another
                                available lesson to change it.
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-4">
                            <span
                              className="rounded-full px-2.5 py-1 text-xs font-bold"
                              style={{
                                backgroundColor: placement.bg,
                                color: placement.fg,
                              }}
                            >
                              {placement.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {reviewStudents.length === 0 &&
                  // Three different empty tables, and they need three different
                  // sentences. Blaming the filters is wrong when there is nobody
                  // to filter, and inviting the teacher to turn it on is wrong
                  // when the roster failed to load -- the button they are being
                  // pointed at is disabled in that state.
                  (reviewLoadFailed ? (
                    <div className="flex flex-col items-center gap-3 p-8 text-center">
                      <p className="text-sm font-semibold text-[#B42318]">
                        Could not load your students, so starting lessons cannot
                        be approved yet.
                      </p>
                      <button
                        type="button"
                        onClick={() => void loadReviewDetails()}
                        className="rounded-[6px] border border-[#D6CEBF] bg-white px-5 py-2.5 text-sm font-bold text-[#28543D] hover:bg-[#EEF0E3]"
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <p className="p-8 text-center text-sm text-[#687368]">
                      {reviewRoster.length === 0
                        ? "No students in your groups yet. You can turn Auto-sequencing on now. Students will be placed as they join."
                        : "No students match these filters."}
                    </p>
                  ))}
              </div>
            </div>

            <div className="border-t border-[#DED8CC] bg-[#FBF8F1] px-6 py-5">
              {/* In the footer, beside the button that retries it, so it is
                  on screen however far the roster above is scrolled. */}
              {dialogError && (
                <div className="mb-4">
                  <DialogError message={dialogError} />
                </div>
              )}
              {/* Activation only. The design's switch review has no
                  sign-off (Alloy, teacher v12): approving is the sign-off. */}
              {!modeSwitch && (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={reviewedAll}
                  onClick={() => setReviewedAll((value) => !value)}
                  className="flex w-full items-start gap-3 rounded-[6px] border border-[#DED8CC] bg-white p-4 text-left"
                >
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border ${reviewedAll ? "border-[#28543D] bg-[#28543D] text-white" : "border-[#9AA884] bg-white text-transparent"}`}
                  >
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-[#294B39]">
                      I reviewed the starting lesson for all students.
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-[#687368]">
                      I confirm that Auto-sequencing can begin from these
                      approved positions.
                    </span>
                  </span>
                </button>
              )}
              <div
                className={`${modeSwitch ? "" : "mt-4 "}flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between`}
              >
                {everyStudentPlaced ? (
                  <p
                    className="text-xs font-semibold"
                    style={{ color: "#28543D" }}
                  >
                    {/* Counted across the whole roster, not the filtered
                        view, because that is what the button does: `activate`
                        posts one activation per classroom for every student in
                        `reviewRoster` and then flips the teacher-wide default.
                        A count that followed the group filter would read "3
                        students have valid starting points" over a button
                        about to enable 22. Reported as "across all groups" so
                        the number stops looking like a filter bug. */}
                    {reviewRoster.length} students across all groups have valid
                    starting points
                  </p>
                ) : (
                  <p
                    className="text-xs font-semibold"
                    style={{ color: "#9A5B00" }}
                  >
                    {/* Named, not counted. "Every student needs a starting
                        lesson" on a 14-student roster says nothing about where
                        to look. */}
                    {unplacedStudents.length} of {reviewRoster.length} students
                    {pointerReview
                      ? " across all groups have no selected starting lesson: "
                      : " across all groups still need a starting lesson: "}
                    {unplacedStudents
                      .slice(0, 3)
                      .map((student) => student.name)
                      .join(", ")}
                    {unplacedStudents.length > 3 &&
                      ` +${unplacedStudents.length - 3} more`}
                    {". "}
                    <button
                      type="button"
                      onClick={() => setReviewOnlyUnplaced((only) => !only)}
                      className="font-bold underline"
                    >
                      {onlyUnplaced ? "Show all students" : "Show only these"}
                    </button>
                  </p>
                )}
                {/* Two equal columns rather than content-width buttons: the
                    primary's label is four times the length of "Cancel", so
                    side by side they read as different kinds of control.
                    `!w-full` and `!h-14` because et-ui hardcodes `w-fit` and
                    sizes its height from padding. Whether an unprefixed
                    `w-full` wins depends on which order Tailwind emits the two
                    width utilities in, which is a property of the build rather
                    than of this file -- the important prefix does not care.

                    `sm:w-fit` rather than a fixed width: at 420px the two 1fr
                    tracks were ~204px each and "Approve and turn on" wrapped
                    onto a second line. Sizing the grid to its content lets the
                    tracks resolve to the wider label's own max-content width
                    and stay equal to each other, so the fit holds at whatever
                    the rendered font actually measures instead of at a number
                    guessed here -- no `whitespace-nowrap` needed, because a
                    track sized to max-content is by definition unwrapped.
                    Measured in Mulish: 420px gave two 204px columns and a
                    two-line label; `w-fit` gives 225px each on one line, still
                    equal. Left full-width below `sm`, where two columns of a
                    narrow viewport genuinely do need to wrap. */}
                <div className="grid grid-cols-2 gap-3 sm:ml-auto sm:w-fit">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => setReviewOpen(false)}
                    className="h-14 w-full rounded-[6px] border border-[#D6CEBF] bg-white text-lg font-bold text-[#687368]"
                  >
                    Cancel
                  </button>
                  <PrimaryButton
                    className="!h-14 !w-full"
                    onClick={() =>
                      void (modeSwitch ? confirmModeSwitch() : activate())
                    }
                    // A switch does not wait for every student: one with no
                    // starting lesson waits for placement (R3), as a student
                    // added later does. It does wait for the roster itself --
                    // still loading or failed, there are no picks to send.
                    disabled={
                      (modeSwitch
                        ? reviewDetails === null || !reviewSeeded
                        : !reviewedAll || !activationReady) ||
                      saving ||
                      !canEditSequencing
                    }
                  >
                    {saving
                      ? modeSwitch
                        ? "Switching…"
                        : "Activating…"
                      : dialogError
                        ? "Try again"
                        : modeSwitch && !teacherEnabled
                          ? "Approve"
                          : "Approve and turn on"}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {skipTarget && primary && (
        <SkipDayModal
          label={formatDayLabel(skipTarget.date)}
          isFuture={skipTarget.isFuture}
          skippingGroups={groupsSkipping(skipTarget.date).map((group) => ({
            name: group.classroom_name,
            someStudents: !(
              group.group_skipped_dates ?? group.skipped_dates
            )?.includes(skipTarget.date),
            hasLessons: holdsLessonsOn(group, skipTarget.date),
          }))}
          totalGroups={skipGroups(skipTarget.date).length}
          groupLabel={
            skipGroups(skipTarget.date).length > 1
              ? skipGroups(skipTarget.date).length ===
                headerGroups(skipTarget.date).length
                ? `All ${skipGroups(skipTarget.date).length} daily groups on screen`
                : `${skipGroups(skipTarget.date).length} of the ${headerGroups(skipTarget.date).length} daily groups on screen`
              : // The one group the skip reaches, which need not be `primary`.
                (skipGroups(skipTarget.date)[0]?.classroom_name ??
                primary.classroom_name)
          }
          leftOut={headerGroups(skipTarget.date)
            .filter(
              (group) =>
                !skipGroups(skipTarget.date).some(
                  (reached) => reached.classroom_id === group.classroom_id,
                ),
            )
            .map((group) => group.classroom_name)}
          groupCount={skipWrites("group", skipTarget.date).reduce(
            (count, write) =>
              count +
              (write.scope === "group"
                ? (dayGroups.find(
                    (group) => group.classroom_id === write.classroomId,
                  )?.students.length ?? 0)
                : write.studentIds.length),
            0,
          )}
          selectedCount={skipWrites("selected", skipTarget.date).reduce(
            (count, write) => count + write.studentIds.length,
            0,
          )}
          saving={saving}
          onConfirm={(scope) => void skipDay(scope)}
          onRemove={() => void removeSkipDay()}
          othersSelectedCount={(() => {
            const skipping = alreadySkipping("selected", skipTarget.date);
            return skipWrites("selected", skipTarget.date)
              .filter((write) => !skipping.has(write.classroomId))
              .reduce((count, write) => count + write.studentIds.length, 0);
          })()}
          offPaceGroups={skipGroups(skipTarget.date)
            .filter(
              (group) =>
                // Still to skip: the groups `skipDay(..., true)` writes to.
                !group.skipped_dates?.includes(skipTarget.date) &&
                !servesWeekday(
                  group.settings.expected_days_per_week,
                  skipTarget.date,
                ) &&
                studentsWithMovableLessonsOn(group, skipTarget.date).length > 0,
            )
            .map((group) => group.classroom_name)}
          onSkipOthers={(scope) => void skipDay(scope, true)}
          onClose={() => setSkipTarget(null)}
        />
      )}

      {shiftConfirm && (
        <ShiftScheduleModal
          direction={shiftConfirm}
          count={selectionStudentIds.length}
          saving={saving}
          onConfirm={() => void shiftSchedules(shiftConfirm)}
          onClose={() => setShiftConfirm(null)}
        />
      )}

      {/* The confirm step is its own dialog, not a section appended to this
          panel: the design stacks a compact card over the roster, and inlining
          it put the summary below the picker and the week preview, off-screen
          on the height the modal already fills. Unmounted rather than hidden,
          so Go back returns to a picker with the same pick still made. */}
      {changeOpen && !changeConfirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-title"
        >
          {/* The design's shape: a wide panel of bordered sections — the chosen
              path, the student's surrounding lessons, the cascade, then the
              disposition — rather than one narrow stack of controls. */}
          <div
            className="mx-4 w-full overflow-y-auto rounded-[8px] border border-[#DED8CC] bg-white shadow-2xl"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            <div className="flex items-center justify-between border-b border-[#DED8CC] px-10 py-8 max-md:px-5 max-md:py-6">
              <div>
                <p className="text-sm font-extrabold uppercase tracking-wide text-[#28543D]">
                  {selectedStudents.size}{" "}
                  {selectedStudents.size === 1 ? "student" : "students"}{" "}
                  selected
                </p>
                <h2
                  id="change-title"
                  className="mt-2 text-3xl font-extrabold text-[#294B39] max-md:text-2xl"
                >
                  {changeStudent
                    ? `Change lesson for ${changeStudent.name}`
                    : `Change lesson for ${selectedStudents.size} students`}
                </h2>
              </div>
              <button
                type="button"
                aria-label="Close change lesson"
                onClick={closeChangeModal}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F3EDE2] text-[#687368]"
              >
                <X size={20} />
              </button>
            </div>

            <div className="border-b border-[#DED8CC] px-10 py-6 max-md:px-5">
              <p className="text-sm font-bold uppercase tracking-wide text-[#687368]">
                Selected curriculum path
              </p>
              <div className="mt-3 text-base">
                <CurriculumBreadcrumb
                  path={selectedOption?.path}
                  fallback={
                    selectedOption?.title ??
                    // Names the reason, not just the absence, so an empty
                    // picker reads as deliberate rather than broken.
                    (selectedStudents.size > 1
                      ? "No lesson selected. Students are currently on different lessons. Choose a lesson below."
                      : "Choose a lesson below")
                  }
                />
              </div>
            </div>

            {changeStudent && (
              <div className="border-b border-[#DED8CC] px-10 py-6 max-md:px-5">
                <p className="text-sm font-bold uppercase tracking-wide text-[#687368]">
                  Selected student path
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {(
                    [
                      ["Previous", changeStudent.previous, false],
                      ["Current", changeStudent.current, true],
                      ["Next", changeStudent.upcoming, false],
                    ] as [string, LessonItem[], boolean][]
                  ).map(([label, lessons, isCurrent]) => (
                    <div
                      key={label}
                      className="rounded-[6px] border p-3"
                      style={{
                        backgroundColor: isCurrent ? "#EEF0E3" : "#FBF8F1",
                        borderColor: isCurrent ? "#28543D" : "#DED8CC",
                      }}
                    >
                      <span
                        className="text-[10px] font-extrabold uppercase tracking-wide"
                        style={{ color: isCurrent ? "#28543D" : "#687368" }}
                      >
                        {label}
                      </span>
                      {/* Every lesson in the bucket, not just the first: at two
                          or three expected lessons per day a bucket holds
                          several, and a teacher confirming a sequencing change
                          needs to see all of what surrounds it. */}
                      <div className="mt-2 grid gap-1.5">
                        {lessons.length ? (
                          lessons.map((lesson, index) => (
                            <CurriculumBreadcrumb
                              key={`${lesson.row_id ?? index}-${lesson.curriculum_node_id ?? lesson.title}`}
                              compact
                              path={lesson.path}
                              fallback={lesson.title}
                            />
                          ))
                        ) : (
                          <CurriculumBreadcrumb compact />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-10 py-8 max-md:px-5 max-md:py-6">
              {/* An empty option list is not a picker with nothing in it, it is
                  a group whose curriculum has no publishable lesson to move to
                  — the sequence is unset, or every slot in it is a placeholder
                  or a retired lesson. Rendering the dropdown anyway put a
                  teacher in front of "No options available" above a Save button
                  that could never enable, with nothing saying why. */}
              {changeLessonOptions.length ? (
                <>
                  <CurriculumPathPicker
                    options={changeLessonOptions}
                    selected={selectedOption}
                    // "First lesson" for the same reason the list below shows:
                    // a one-a-day pick that replans the days after it is still
                    // the first of many, even though its batch is one.
                    picksFirstOfMany={
                      (weekPreview?.lessons_per_unit ?? 1) > 1 ||
                      previewSpansDays
                    }
                    onChange={(option) => {
                      setTargetNode(option.curriculum_node_id);
                      setChangeConfirming(false);
                    }}
                  />
                  {/* The week the pick produces. The teacher chooses only the
                      first lesson; the rest follow it in sequence order, and
                      showing them is what stops Save being a guess.

                      Spanning days counts as "more than one lesson" for this
                      gate. A one-a-day classroom has `lessons_per_unit === 1`,
                      so the pace test alone hid the list entirely -- and Save
                      still replans every later day, which is exactly the thing
                      the teacher was given no indication of. The batch is one
                      lesson; the change is not. */}
                  {weekPreview &&
                    (weekPreview.lessons_per_unit > 1 || previewSpansDays) && (
                      <section className="mt-8 border-t border-[#DED8CC] pt-6">
                        <div className="flex items-start justify-between gap-4">
                          <p className="text-sm font-bold uppercase tracking-wide text-[#687368]">
                            {previewSpansDays
                              ? "Lessons from this day on"
                              : `Lessons this ${weekPreview.unit}`}
                          </p>
                          <span className="shrink-0 text-sm font-bold text-[#28543D]">
                            {weekPreview.lessons_per_unit} per{" "}
                            {weekPreview.unit}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-[#687368]">
                          {previewSpansDays ? (
                            <>
                              You choose the first lesson. The days below are
                              populated automatically in sequence.
                            </>
                          ) : (
                            <>
                              You choose the first lesson. The rest of the{" "}
                              {weekPreview.unit} is populated automatically in
                              sequence.
                            </>
                          )}
                        </p>
                        {/* A window can be shorter than the pace: work already
                          finished stays in it and spends a slot, and the
                          curriculum can run out. Saying so beats a teacher
                          counting cards against the number above. */}
                        {weekPreview.target < weekPreview.lessons_per_unit && (
                          <p className="mt-1 text-xs font-semibold text-[#9A5B00]">
                            {weekPreview.target} of{" "}
                            {weekPreview.lessons_per_unit} — the sequence has no
                            more lessons after this window.
                          </p>
                        )}
                        <ol className="mt-4 grid gap-2">
                          {weekPreview.lessons.map((lesson, index) => {
                            const chosen = lesson.source === "teacher_choice";
                            // A heading each time the day changes, so a list that
                            // spans days reads as the days it covers rather than
                            // as one long batch on the day being edited. Kept
                            // inside the same `ol` -- the rows stay one ordered
                            // list, numbered by the slot the server assigned.
                            const startsADay =
                              previewSpansDays &&
                              lesson.day !== null &&
                              (index === 0 ||
                                weekPreview.lessons[index - 1].day !==
                                  lesson.day);
                            return (
                              <Fragment key={lesson.curriculum_node_id}>
                                {startsADay && (
                                  <li className="mt-2 text-xs font-bold uppercase tracking-wide text-[#687368] first:mt-0">
                                    {formatDayLabel(lesson.day)}
                                  </li>
                                )}
                                <li
                                  className="flex items-center gap-3 rounded-[6px] border px-4 py-3"
                                  style={{
                                    backgroundColor: chosen
                                      ? "#EEF0E3"
                                      : "#F3EDE2",
                                    borderColor: chosen ? "#9AA884" : "#DED8CC",
                                  }}
                                >
                                  <span
                                    aria-hidden="true"
                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-extrabold"
                                    style={{
                                      backgroundColor: chosen
                                        ? "#28543D"
                                        : "#DED8CC",
                                      color: chosen ? "#fff" : "#687368",
                                    }}
                                  >
                                    {lesson.slot}
                                  </span>
                                  <span className="flex-1 text-base font-bold text-[#294B39]">
                                    {lesson.title}
                                  </span>
                                  {lesson.already_completed && (
                                    <span className="shrink-0 rounded-full bg-[#EEF0E3] px-3 py-1 text-xs font-bold text-[#28543D]">
                                      Already completed
                                    </span>
                                  )}
                                  <span
                                    className="shrink-0 rounded-full px-3 py-1 text-xs font-bold"
                                    style={{
                                      backgroundColor: chosen
                                        ? "#fff"
                                        : "transparent",
                                      color: chosen ? "#28543D" : "#8A958F",
                                      border: chosen
                                        ? "1px solid #9AA884"
                                        : "1px solid transparent",
                                    }}
                                  >
                                    {chosen ? "Your choice" : "Auto-filled"}
                                  </span>
                                </li>
                              </Fragment>
                            );
                          })}
                        </ol>
                      </section>
                    )}
                  {previewError && (
                    <p
                      role={previewError === "rejected" ? "alert" : "status"}
                      className="mt-6 text-sm font-semibold text-[#9A5B00]"
                    >
                      {previewError === "rejected"
                        ? "This lesson cannot be assigned from the current sequence. Pick a different one."
                        : "Could not load the lesson list for this pick. The pick itself is unaffected \u2014 the server rebuilds the list when you save."}
                    </p>
                  )}
                </>
              ) : (
                <div
                  role="status"
                  className="rounded-[8px] border border-[#F2D9A8] bg-[#FFF8EC] px-5 py-4 text-sm font-semibold leading-6 text-[#9A5B00]"
                >
                  There are no lessons to choose from for this group. Its
                  curriculum sequence is either not set up, or holds no
                  published lesson. Either way there is nothing to move{" "}
                  {changeStudent ? changeStudent.name : "these students"} to.
                  Ask an administrator to check the curriculum for this group.
                </div>
              )}
            </div>

            {/* One checkbox rather than a radio pair: checked keeps the lesson
                finishable, unchecked marks it skipped — the design states both
                outcomes in the same line instead of making them two options.
                Not in No expectation mode, which has no skip (R19): a lesson
                started and moved past is Incomplete, and nothing else is set
                aside. */}
            {!pointerMode && (
              <div className="border-t border-[#DED8CC] px-10 py-5 max-md:px-5">
                <label className="flex items-center gap-3 text-sm text-[#294B39]">
                  <input
                    type="checkbox"
                    checked={disposition === "push_incomplete"}
                    onChange={(event) => {
                      setDisposition(
                        event.target.checked ? "push_incomplete" : "skip",
                      );
                      setChangeConfirming(false);
                    }}
                    className="h-5 w-5 shrink-0 accent-[#28543D]"
                  />
                  <span className="min-w-0">
                    <span className="font-bold">
                      {selectedStudents.size === 1
                        ? "Push current lesson to Incomplete pile"
                        : "Push each student’s current lesson to their Incomplete pile"}
                    </span>
                    <span className="ml-2 text-[#687368]">
                      Checked:{" "}
                      {changeStudent ? changeStudent.name : "each student"} can
                      still finish it later. Unchecked: it moves to Skipped.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Only a save that failed after Go back lands here: going back
                clears one shown on the confirm step, where the save is made. */}
            {dialogError && (
              <div className="border-t border-[#DED8CC] px-10 py-5 max-md:px-5">
                <DialogError message={dialogError} />
              </div>
            )}

            <div className="flex justify-end gap-4 border-t border-[#DED8CC] bg-[#FBF8F1] px-10 py-6 max-md:px-5">
              <button
                type="button"
                onClick={closeChangeModal}
                className="h-14 rounded-[6px] border border-[#D6CEBF] bg-white px-8 text-lg font-bold text-[#687368]"
              >
                Cancel
              </button>
              <PrimaryButton
                onClick={() => setChangeConfirming(true)}
                // Same gate as the preview, plus its verdict. A preselected
                // node the picker cannot show is not a pick, and a pick the
                // preview got a 422 for fails the identical check on save --
                // Save was enabled through both, which is what made the
                // fallback copy promise a save that could not succeed.
                disabled={
                  !selectedOption || saving || previewError === "rejected"
                }
              >
                Save
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Alloy's SequencingWarningModal: a compact card over the roster with
          its own Go back / Confirm change, shown instead of the picker rather
          than beneath it. */}
      {changeOpen && changeConfirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#294B39]/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-confirm-title"
        >
          {/* Capped and scrollable, as the picker panel this replaces already
              is. The overlay is `fixed` and centres its child, so anything
              taller than the viewport is clipped at both ends with nothing to
              scroll -- on a phone in landscape the icon, heading, table and
              action row together exceed the height, and Go back / Confirm
              change fall off the bottom unreachable. */}
          <div
            className="mx-4 w-full max-w-lg overflow-y-auto rounded-[8px] border border-[#DED8CC] bg-white p-6 text-[#294B39] shadow-2xl"
            style={{ maxHeight: "calc(100vh - 2rem)" }}
          >
            <div
              className="flex h-12 w-12 items-center justify-center rounded-[6px] bg-[#FFF4E5] text-[#9A5B00]"
              aria-hidden="true"
            >
              <FileText size={22} />
            </div>
            <h2
              id="change-confirm-title"
              className="mt-4 text-2xl font-bold text-[#294B39]"
            >
              {changeImpact.length > 1
                ? "Apply group-wide change?"
                : "Alter lesson sequencing?"}
            </h2>
            {changeImpact.length > 1 && (
              <p className="mt-2 inline-block rounded-full bg-[#FFF4E5] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-[#9A5B00]">
                {changeImpact.length} students affected
              </p>
            )}
            <p className="mt-3 text-sm leading-6 text-[#687368]">
              {changeImpact.length > 1
                ? `All ${changeImpact.length} selected students will be moved to the same current lesson, and each student\u2019s next lesson will be automatically recalculated. Completed lesson history will not be changed.`
                : "This will change the current lesson and automatically recalculate the next lesson for 1 student. Completed lesson history will not be changed."}
            </p>
            {/* Named per student rather than counted. A select-all over a
                drifted roster moves people off lessons the teacher has not
                looked at, and a bare count cannot show that. */}
            <div className="mt-5 overflow-hidden rounded-[6px] border border-[#DED8CC]">
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full min-w-[420px] border-collapse text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-[#FBF8F1] text-[11px] font-extrabold uppercase tracking-wide text-[#687368]">
                    <tr>
                      <th scope="col" className="px-4 py-2.5">
                        Student
                      </th>
                      <th scope="col" className="px-3 py-2.5">
                        Old lesson
                      </th>
                      <th scope="col" className="px-3 py-2.5">
                        New lesson
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeImpact.map((row) => (
                      <tr
                        key={row.studentId}
                        className="border-t border-[#DED8CC]"
                      >
                        <td className="px-4 py-2.5 font-bold text-[#294B39]">
                          {row.name}
                        </td>
                        <td className="px-3 py-2.5 text-[#687368]">
                          {row.fromTitle}
                        </td>
                        <td
                          className="px-3 py-2.5 font-bold"
                          style={{
                            color: row.unchanged ? "#687368" : "#28543D",
                          }}
                        >
                          {selectedOption?.title ?? ""}
                          {row.unchanged && (
                            <span className="ml-2 rounded-full bg-[#F3EDE2] px-2 py-0.5 text-[10px] font-bold text-[#687368]">
                              No change
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {dialogError && (
              <div className="mt-5">
                <DialogError message={dialogError} />
              </div>
            )}

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setChangeConfirming(false)}
                className="rounded-[6px] border border-[#D6CEBF] bg-white px-6 py-4 font-bold text-[#687368]"
              >
                Go back
              </button>
              <PrimaryButton
                onClick={() => void changeLesson()}
                disabled={saving}
              >
                {saving
                  ? "Saving\u2026"
                  : dialogError
                    ? "Try again"
                    : "Confirm change"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** What anyone but the owning teacher sees here (R31). */
const SequencingOwnerOnly = () => (
  <div className="flex flex-col gap-3 font-mulish text-[#294B39]">
    <h1 className="text-2xl font-bold">Lesson sequencing</h1>
    <div
      role="status"
      className="rounded-[8px] border border-[#F2D9A8] bg-[#FFF8EC] px-5 py-4 text-sm font-semibold text-[#9A5B00]"
    >
      Lesson sequencing is only available to the teacher who owns the class.
      Switch back to your own account to manage sequencing for your groups.
    </div>
  </div>
);

/**
 * The page belongs to the teacher who owns the classes (P-PRD-525 R31). A
 * co-teacher or org admin acting for another teacher gets a notice rather than
 * the page: the server refuses them every read and write on it, so rendering
 * it would only show a failed load. Nothing is fetched for them.
 *
 * A failed accounts lookup decides nothing either way, so the page loads and
 * the server answers: it serves the owner, and refuses anyone else the
 * overview, which the page reports as a failed load. Writes are refused the
 * same way, so the controls stay usable rather than shut an owner out.
 */
const LessonSequencingPage = () => {
  const actingTeacherId = useAuthStore((state) => state.actingTeacherId);
  const isOrgAdmin = useAuthStore((state) => state.user?.role === "ORG_ADMIN");
  const { accounts, isLoading, error } = useAccounts();
  const ownership = sequencingOwnership({
    actingTeacherId,
    isOrgAdmin,
    accounts,
    accountsLoading: Boolean(isLoading),
    accountsFailed: error != null,
  });
  if (ownership === "unknown") return <LessonSequencingSkeleton />;
  if (ownership === "not_owner") return <SequencingOwnerOnly />;
  return (
    <LessonSequencingPageContent
      accessibleAccounts={accounts}
      canEditSequencing
    />
  );
};

export default LessonSequencingPage;
